import { useState, useEffect, useContext } from 'react';
import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CogIcon } from '@heroicons/react/20/solid';
import LoadingComponent from './loading';
import StaffNav from './managernav';
import MeetingCalendar from './meetingscalenderlite';
import Assessment from './assessment';
import { format } from 'date-fns';
import { AuthContext } from '../AuthContext';
import StaffCosts from './staffcosts';

function PDFScanner() {
  const [activeTab, setActiveTab] = useState('scan');
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [loading, setLoading] = useState(false);
  const [apiResponse, setApiResponse] = useState(null);
  const [roles, setRoles] = useState([]);
  const [vacancies, setVacancies] = useState([]);
  const [selectedVacancyId, setSelectedVacancyId] = useState(null);
  const [pdfText, setPdfText] = useState('');
  const [resumeId, setResumeId] = useState(null);

  const navigate = useNavigate()
  const location = useLocation();

  const user = useContext(AuthContext);

  const managerEmail = user?.email || '';
  

  // Interview form state
  const [showInterviewForm, setShowInterviewForm] = useState(false);
  const [interviewData, setInterviewData] = useState({
    name: '',
    role: '',
    date: '',
    time: '',
    location: '',
    vacancyId: null,
    email: '',
  });

  // Interviews list state
  const [interviews, setInterviews] = useState([]);
  const [loadingInterviews, setLoadingInterviews] = useState(false);

  // Per-interview UI state
  const [showQuestions, setShowQuestions] = useState({});
  const [editingQuestionId, setEditingQuestionId] = useState({});
  const [editingQuestionText, setEditingQuestionText] = useState({});
  const [generatingQuestions, setGeneratingQuestions] = useState({});

  // For expanded vacancies in interview tab
  const [expandedVacancyIds, setExpandedVacancyIds] = useState({});

  // Decision modal state
  const [decisionModal, setDecisionModal] = useState({
    open: false,
    interview: null,
    passed: null,
    notes: '',
    saving: false,
  });

  // Shortlist state
  const [shortlistedResumes, setShortlistedResumes] = useState([]);
  const [loadingShortlist, setLoadingShortlist] = useState(false);
  const [onboardingCandidates, setOnboardingCandidates] = useState([]);
  const [loadingOnboarding, setLoadingOnboarding] = useState(false);
  const [savingOnboardingId, setSavingOnboardingId] = useState(null);
  const [creatingOnboardingUserId, setCreatingOnboardingUserId] = useState(null);
  const [candidateWorkEmail, setCandidateWorkEmail] = useState({});
  const [startDateDraftByVacancy, setStartDateDraftByVacancy] = useState({});
  const [departments, setDepartments] = useState([]);
  const [selectedRoleByVacancy, setSelectedRoleByVacancy] = useState({});
  const [selectedDepartmentByVacancy, setSelectedDepartmentByVacancy] = useState({});
  const [assessmentRecord, setAssessmentRecord] = useState(null);
  const [assessmentModalOpen, setAssessmentModalOpen] = useState(false);
  const [assessmentLoading, setAssessmentLoading] = useState(false);
  const [assessmentError, setAssessmentError] = useState(null);
  const [subscriptionModal, setSubscriptionModal] = useState(false);

  const normalizeText = (value) => (value || '').toString().trim().toLowerCase();
  const normalizeManagementLevel = (value) => {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed >= 1 && parsed <= 4 ? parsed : 1;
  };
  const resolveAccessFromRole = (role) => {
    const normalizedAccess = String(role?.Access || '').trim().toLowerCase();
    if (['admin', 'manager', 'staff'].includes(normalizedAccess)) {
      return normalizedAccess;
    }

    const resolvedManagementLevel = normalizeManagementLevel(role?.ManagementLevel);
    if (resolvedManagementLevel >= 4) return 'admin';
    if (resolvedManagementLevel > 1) return 'manager';
    return 'staff';
  };
  const toDateInputValue = (value) => {
    if (!value) return '';
    if (typeof value === 'string') {
      const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
      if (match) return match[1];
    }

    try {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return '';
      return date.toISOString().slice(0, 10);
    } catch {
      return '';
    }
  };

  // Fetch roles and vacancies
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await fetch('https://api.insightos.co.uk/api/allroles', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to fetch roles');
        const data = await response.json();
        setRoles(Array.isArray(data.data) ? data.data : []);
      } catch (error) {
        setRoles([]);
      }
    };
    const fetchVacancies = async () => {
      try {
        const response = await fetch('https://api.insightos.co.uk/api/vacancies', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to fetch vacancies');
        const data = await response.json();
        setVacancies(Array.isArray(data.data) ? data.data : []);
      } catch (error) {
        setVacancies([]);
      }
    };
    const fetchDepartments = async () => {
      try {
        const response = await fetch('https://api.insightos.co.uk/api/getdepartments', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        if (!response.ok) throw new Error('Failed to fetch departments');
        const data = await response.json();
        setDepartments(Array.isArray(data.data) ? data.data : []);
      } catch (error) {
        setDepartments([]);
      }
    };
    fetchRoles();
    fetchVacancies();
    fetchDepartments();
  }, []);

  useEffect(() => {
    if (!onboardingCandidates.length) return;

    setSelectedRoleByVacancy((prev) => {
      const next = { ...prev };
      onboardingCandidates.forEach((candidate) => {
        if (next[candidate.id]) return;
        const matchedRole = roles.find(
          (role) => normalizeText(role?.Name) === normalizeText(candidate?.Role)
        );
        if (matchedRole?.id) {
          next[candidate.id] = matchedRole.id;
        }
      });
      return next;
    });

    setSelectedDepartmentByVacancy((prev) => {
      const next = { ...prev };
      onboardingCandidates.forEach((candidate) => {
        if (next[candidate.id]) return;
        const matchedRole = roles.find(
          (role) => normalizeText(role?.Name) === normalizeText(candidate?.Role)
        );
        if (!matchedRole?.Department) return;
        const matchedDepartment = departments.find(
          (department) => normalizeText(department?.Name) === normalizeText(matchedRole.Department)
        );
        if (matchedDepartment?.id) {
          next[candidate.id] = matchedDepartment.id;
        }
      });
      return next;
    });

    setStartDateDraftByVacancy((prev) => {
      const next = { ...prev };
      const validCandidateIds = new Set();

      onboardingCandidates.forEach((candidate) => {
        validCandidateIds.add(String(candidate.id));
        next[candidate.id] = toDateInputValue(candidate.StartDate);
      });

      Object.keys(next).forEach((candidateId) => {
        if (!validCandidateIds.has(String(candidateId))) {
          delete next[candidateId];
        }
      });

      return next;
    });
  }, [onboardingCandidates, roles, departments]);

  useEffect(() => {
    if (activeTab === 'interviews') {
      fetchInterviews();
    } else if (activeTab === 'shortlist') {
      fetchShortlistedResumes();
    } else if (activeTab === 'onboarding') {
      fetchOnboardingCandidates();
    }
    // eslint-disable-next-line
  }, [activeTab]);

  const fetchInterviews = async () => {
    setLoadingInterviews(true);
    try {
      const response = await fetch('https://api.insightos.co.uk/api/getinterviews', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch interviews');
      const data = await response.json();
      const normalizedInterviews = Array.isArray(data.data)
        ? data.data.map((interview) => ({
            ...interview,
            Passed:
              interview?.Passed === null || interview?.Passed === undefined
                ? null
                : Number(interview.Passed),
            Accepted:
              interview?.Accepted === null || interview?.Accepted === undefined
                ? null
                : Number(interview.Accepted),
            Complete:
              interview?.Complete === null || interview?.Complete === undefined
                ? null
                : Number(interview.Complete),
          }))
        : [];
      setInterviews(normalizedInterviews);
      console.log('Fetched interviews:', data.data);
    } catch (error) {
      setInterviews([]);
    } finally {
      setLoadingInterviews(false);
    }
  };

  useEffect(() => {
    if (!location.state?.heavyAiRefreshKey) {
      return;
    }

    const nextTab = location.state?.activeTab || 'interviews';
    const interviewId =
      location.state?.revealInterviewId || location.state?.heavyAiJobResult?.interviewId;

    setActiveTab(nextTab);
    if (interviewId) {
      setShowQuestions((prev) => ({ ...prev, [interviewId]: true }));
    }
    fetchInterviews();
  }, [location.state?.heavyAiRefreshKey]);

  const fetchShortlistedResumes = async () => {
    setLoadingShortlist(true);
    try {
      const allResumes = [];
      for (const vacancy of vacancies) {
        const response = await fetch(`https://api.insightos.co.uk/api/resume?vacancyId=${vacancy.id}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        if (response.ok) {
          const data = await response.json();
          if (data.resumes && Array.isArray(data.resumes)) {
            // Filter out rejected resumes and add vacancy info
            const filtered = data.resumes
              .filter(r => r.Status !== 'Rejected')
              .map(r => ({ ...r, vacancy }));
            allResumes.push(...filtered);
          }
        }
      }
      setShortlistedResumes(allResumes);
    } catch (error) {
      console.error('Failed to fetch shortlisted resumes:', error);
      setShortlistedResumes([]);
    } finally {
      setLoadingShortlist(false);
    }
  };

  const fetchOnboardingCandidates = async () => {
    setLoadingOnboarding(true);
    try {
      const response = await fetch('https://api.insightos.co.uk/api/onboarding-candidates', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch onboarding candidates');
      const data = await response.json();
      const normalized = Array.isArray(data.data)
        ? data.data.map((row) => ({
            ...row,
            NeedDBS: Number(row?.NeedDBS || 0),
            AcceptedPosition:
              row?.AcceptedPosition === null || row?.AcceptedPosition === undefined
                ? null
                : Number(row.AcceptedPosition),
            NewUserCreated:
              row?.NewUserCreated === null || row?.NewUserCreated === undefined
                ? null
                : Number(row.NewUserCreated),
          }))
        : [];
      setOnboardingCandidates(normalized);
      const emailMap = {};
      normalized.forEach((row) => {
        emailMap[row.id] = row.Email || '';
      });
      setCandidateWorkEmail(emailMap);
    } catch (error) {
      setOnboardingCandidates([]);
    } finally {
      setLoadingOnboarding(false);
    }
  };

  const updateOnboardingCandidate = async (vacancyId, payload) => {
    setSavingOnboardingId(vacancyId);
    try {
      const response = await fetch(`https://api.insightos.co.uk/api/onboarding/${vacancyId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        throw new Error(data.message || 'Failed to update onboarding');
      }
      await fetchOnboardingCandidates();
    } catch (error) {
      alert(error.message || 'Failed to update onboarding');
    } finally {
      setSavingOnboardingId(null);
    }
  };

  const createOnboardingUser = async (vacancyId, resumeId, startDate) => {
    if (!resumeId) {
      alert('No candidate resume found for this vacancy.');
      return;
    }
    const workEmail = (candidateWorkEmail[vacancyId] || '').trim();
    if (!workEmail) {
      alert('Please add a work email before creating the user.');
      return;
    }
    if (!startDate) {
      alert('Please add a candidate start date before creating the user.');
      return;
    }

    const roleId = selectedRoleByVacancy[vacancyId];
    if (!roleId) {
      alert('Please select the role before creating the user.');
      return;
    }

    const departmentId = selectedDepartmentByVacancy[vacancyId];
    const selectedRole = roles.find((role) => Number(role.id) === Number(roleId));
    if (!departmentId) {
      alert('Please select the department before creating the user.');
      return;
    }
    if (!selectedRole) {
      alert('Please select a valid role before creating the user.');
      return;
    }

    const resolvedManagementLevel = normalizeManagementLevel(selectedRole.ManagementLevel);
    const resolvedAccess = resolveAccessFromRole(selectedRole);

    // Quota check — ensure company has capacity for another user
    try {
      const [companyRes, staffRes] = await Promise.all([
        fetch('https://api.insightos.co.uk/api/company', { credentials: 'include' }),
        fetch('https://api.insightos.co.uk/api/staff', { credentials: 'include' }),
      ]);
      const companyData = companyRes.ok ? await companyRes.json() : {};
      const staffData = staffRes.ok ? await staffRes.json() : {};
      const maxUsers = Number(companyData?.data?.[0]?.Users || 0);
      const currentStaff = Array.isArray(staffData?.data) ? staffData.data.length : 0;
      if (maxUsers > 0 && currentStaff >= maxUsers) {
        setSubscriptionModal(true);
        return;
      }
    } catch {
      // Allow through on network error
    }

    setCreatingOnboardingUserId(vacancyId);
    try {
      const response = await fetch('https://api.insightos.co.uk/api/candidatenewuser', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          resumeId,
          email: workEmail,
          startDate,
          roleId,
          departmentId,
          Access: resolvedAccess,
          ManagementLevel: resolvedManagementLevel,
          vacancyId,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        throw new Error(data.message || 'Failed to prepare candidate new user');
      }
      alert('New User Created.');
      navigate('/users');
    } catch (error) {
      alert(error.message || 'Failed to prepare candidate new user');
    } finally {
      setCreatingOnboardingUserId(null);
    }
  };

  const handleStartDateDraftChange = (vacancyId, value) => {
    setStartDateDraftByVacancy((prev) => ({
      ...prev,
      [vacancyId]: value,
    }));
  };

  const commitStartDateChange = async (vacancyId, currentStartDate) => {
    const draftValue = startDateDraftByVacancy[vacancyId] || '';
    const persistedValue = toDateInputValue(currentStartDate);

    if (draftValue === persistedValue) {
      return;
    }

    await updateOnboardingCandidate(vacancyId, { startDate: draftValue || null });
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    setFile(file);
    setFileName(file ? file.name : '');
  };

  const handleSelectVacancy = (vacancyId) => {
    setSelectedVacancyId(vacancyId);
  };

  const handleSubmit = async () => {
    if (!file || !selectedVacancyId) return;
    setLoading(true);
    setApiResponse(null);

    // Find the selected vacancy and its matching role
    const vacancy = vacancies.find(v => v.id === selectedVacancyId);
    const role = roles.find(r => r.Name === vacancy.Role);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('roleId', role ? role.id : '');

      const response = await fetch('https://api.insightos.co.uk/api/cv-scanner', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Failed to process PDF');
      const data = await response.json();
      setApiResponse(data.data || { score: null, feedback: 'No response from backend.' });
      setPdfText(data.pdf || '');
      setResumeId(data.resumeId || null);
    } catch (error) {
      setApiResponse({ score: null, feedback: 'Error: ' + error.message });
    } finally {
      setLoading(false);
    }
  };

  const handleInterviewInputChange = (e) => {
    const { name, value } = e.target;
    setInterviewData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOpenOutlook = () => {
    const recipient = (interviewData.email || '').trim();
    if (!recipient) {
      alert('No candidate email available for this interview.');
      return;
    }

    const subject = encodeURIComponent('Interview Invitation');
    const body = encodeURIComponent(
      `You are invited for an interview.\n\nName: ${interviewData.name}\nRole: ${interviewData.role}\nDate: ${interviewData.date}\nTime: ${interviewData.time}\nLocation: ${interviewData.location}`
    );
    const ccParam = managerEmail ? `&cc=${encodeURIComponent(managerEmail)}` : '';

    window.open(`mailto:${encodeURIComponent(recipient)}?subject=${subject}&body=${body}${ccParam}`);
  };

  const handleSaveInterview = async () => {
    try {
      const payload = { ...interviewData, vacancyId: selectedVacancyId, resumeId };
      const response = await fetch('https://api.insightos.co.uk/api/setinterview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error('Failed to save interview');
      await response.json();
      alert('Interview saved successfully!');
      setShowInterviewForm(false);
      fetchInterviews();
    } catch (error) {
      alert('Failed to save interview: ' + error.message);
    } finally {
      setInterviewData({
        name: '',
        role: '',
        date: '',
        time: '',
        location: '',
        vacancyId: null,
        email: '',
      });
      setSelectedVacancyId(null);
      setFile(null);
      setFileName('');
    }
  };

  const handleSendAssessmentFromShortlist = async (resume) => {
    if (!resume || !resume.id) {
      alert('No resume ID available');
      return;
    }

    try {
      const response = await fetch('https://api.insightos.co.uk/api/sendassessment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json',
            Authorization: `Bearer ${user.analyticsToken}`
         },
        credentials: 'include',
        body: JSON.stringify({ resumeId: resume.id }),
      });
      if (!response.ok) throw new Error('Failed to send assessment');
      await response.json();
      alert('Assessment sent successfully!');
      fetchShortlistedResumes();
    } catch (error) {
      alert('Failed to send assessment: ' + error.message);
    }
  };


  // Handle reject button
  const handleReject = () => {
    setApiResponse(null);
    setShowInterviewForm(false);
    setInterviewData({
      name: '',
      role: '',
      date: '',
      time: '',
      location: '',
      vacancyId: null,
      email: '',
    });
    setFile(null);
    setFileName('');
    setPdfText('');
    setSelectedVacancyId(null);
    setResumeId(null);
  };

  const handleShortlist = async () => {
    if (!resumeId) {
      alert('No resume ID available');
      return;
    }
    try {
      const response = await fetch('https://api.insightos.co.uk/api/shortlist', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ resumeId }),
      });
      if (!response.ok) throw new Error('Failed to shortlist candidate');
      await response.json();
      alert('Candidate shortlisted successfully!');
      handleReject(); // Clear the form
    } catch (error) {
      alert('Failed to shortlist candidate: ' + error.message);
    }
  };

  const handleRejectFromShortlist = async (resumeId) => {
    if (!resumeId || !window.confirm('Reject this candidate?')) return;
    try {
      const response = await fetch(`https://api.insightos.co.uk/api/shortlist`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ resumeId, status: 'Rejected' }),
      });
      if (!response.ok) throw new Error('Failed to update resume status');
      
      fetchShortlistedResumes();
    } catch (error) {
      alert('Failed to reject candidate: ' + error.message);
    }
  };

  const handleScheduleInterviewFromShortlist = (resume) => {
    // Set interview data
    setInterviewData({
      name: resume.CandidateName || '',
      role: resume.vacancy?.Role || '',
      date: '',
      time: '',
      location: 'Meeting Room',
      vacancyId: resume.VacancyId,
      email: resume.Email || '',
    });
    
    // Set the selected vacancy
    setSelectedVacancyId(resume.VacancyId);
    
    // Set PDF text (from About field or empty)
    setPdfText(
      [resume.About, resume.Qualifications, resume.Experience]
        .filter(Boolean)
        .join('\n\n') ||
      ''
    );
    setResumeId(resume.id || resume.Id || null);
    
    // Show the interview form (stay in shortlist tab)
    setShowInterviewForm(true);
  };

  useEffect(() => {
    if (selectedVacancyId) {
      const vacancy = vacancies.find((v) => v.id === selectedVacancyId);
      setInterviewData((prev) => ({
        ...prev,
        role: vacancy ? vacancy.Role : '',
        vacancyId: selectedVacancyId,
      }));
    }
  }, [selectedVacancyId, vacancies]);

  const handleQuestionGeneration = async (interviewId) => {
    if (!interviewId) return;
    setGeneratingQuestions((prev) => ({ ...prev, [interviewId]: true }));
    try {
      const response = await fetch(`https://api.insightos.co.uk/api/generateintquestions/${interviewId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      const result = await response.json();

      if (response.status === 202 && result.queued && result.jobId) {
        navigate(`/job-progress/${result.jobId}`, {
          state: {
            title: 'Generating interview questions',
            successPath: '/recruitment',
            backPath: '/recruitment',
            successState: {
              activeTab: 'interviews',
              revealInterviewId: interviewId,
            },
          },
        });
        return;
      }

      if (!response.ok) throw new Error('Failed to generate questions');
      await fetchInterviews();
      // Auto-show questions after generation
      setShowQuestions((prev) => ({ ...prev, [interviewId]: true }));
    } catch (error) {
      alert('Error generating questions: ' + error.message);
    } finally {
      setGeneratingQuestions((prev) => ({ ...prev, [interviewId]: false }));
    }
  };

  // Per-interview question edit handlers
  const handleToggleShowQuestions = (interviewId) => {
    setShowQuestions((prev) => ({
      ...prev,
      [interviewId]: !prev[interviewId],
    }));
  };

  const handleEditQuestion = (interviewId, question) => {
    setEditingQuestionId((prev) => ({
      ...prev,
      [interviewId]: question.id,
    }));
    setEditingQuestionText((prev) => ({
      ...prev,
      [interviewId]: question.Question,
    }));
  };

  const handleChangeEditingQuestionText = (interviewId, value) => {
    setEditingQuestionText((prev) => ({
      ...prev,
      [interviewId]: value,
    }));
  };

  const handleSaveQuestion = async (interviewId, question) => {
    try {
      await fetch(`https://api.insightos.co.uk/api/updatequestion/${question.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ Question: editingQuestionText[interviewId] }),
      });
      fetchInterviews();
      handleCancelEditQuestion(interviewId);
    } catch (e) {
      alert('Failed to save question');
    }
  };

  const handleCancelEditQuestion = (interviewId) => {
    setEditingQuestionId((prev) => {
      const updated = { ...prev };
      delete updated[interviewId];
      return updated;
    });
    setEditingQuestionText((prev) => {
      const updated = { ...prev };
      delete updated[interviewId];
      return updated;
    });
  };

  const handleDeleteQuestion = async (interviewId, question) => {
    if (!window.confirm('Delete this question?')) return;
    try {
      await fetch(`https://api.insightos.co.uk/api/deletequestion/${question.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      fetchInterviews();
    } catch (e) {
      alert('Failed to delete question');
    }
  };

  // Toggle expanded vacancy in interview tab
  const handleToggleVacancyExpand = (vacancyId) => {
    setExpandedVacancyIds((prev) => ({
      ...prev,
      [vacancyId]: !prev[vacancyId],
    }));
  };

  // Decision modal handlers
  const openDecisionModal = (interview) => {
    setDecisionModal({
      open: true,
      interview,
      passed: null,
      notes: interview.Notes || '',
      saving: false,
    });
  };

  const closeDecisionModal = () => {
    setDecisionModal({
      open: false,
      interview: null,
      passed: null,
      notes: '',
      saving: false,
    });
  };

  const handleDecisionChange = (e) => {
    const { name, value } = e.target;
    setDecisionModal((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSaveDecision = async () => {
    setDecisionModal((prev) => ({ ...prev, saving: true }));
    try {
      const payload = {
        Passed: decisionModal.passed === 'yes' ? 1 : 0,
        Notes: decisionModal.notes,
      };
      const response = await fetch(`https://api.insightos.co.uk/api/interviewdecision/${decisionModal.interview.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error('Failed to save decision');
      closeDecisionModal();
      fetchInterviews();
    } catch (e) {
      alert('Failed to save decision');
      setDecisionModal((prev) => ({ ...prev, saving: false }));
    }
  };

  const handleDeleteInterview = async (interviewId) => {
    if (!window.confirm('Cancel this interview?')) return;
    try {
      await fetch(`https://api.insightos.co.uk/api/deleteinterview/${interviewId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      fetchInterviews();
    } catch (e) {
      alert('Failed to cancel interview');
    }
  };

  const handleCandidateAccepted = async (interviewId) => {
    if (!window.confirm('Mark this candidate as accepted?')) return;
    try {
      const response = await fetch(`https://api.insightos.co.uk/api/acceptcandidate/${interviewId}`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to accept candidate');
      fetchInterviews();
    } catch (e) {
      alert('Failed to accept candidate');
    }
  };

  const handleCandidateRejected = async (interviewId) => {
    if (!window.confirm('Mark this candidate as rejected?')) return;
    try {
      const response = await fetch(`https://api.insightos.co.uk/api/rejectcandidate/${interviewId}`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to reject candidate');
      fetchInterviews();
    } catch (e) {
      alert('Failed to reject candidate');
    }
  };

  // Expanded CV state for shortlist
  const [expandedCVId, setExpandedCVId] = useState(null);

  const handleToggleCV = (resumeId) => {
    setExpandedCVId(expandedCVId === resumeId ? null : resumeId);
  };

  // Group resumes by vacancy
  const resumesByVacancy = {};
  shortlistedResumes.forEach(resume => {
    const vacancyId = resume.VacancyId;
    if (!resumesByVacancy[vacancyId]) {
      resumesByVacancy[vacancyId] = [];
    }
    resumesByVacancy[vacancyId].push(resume);
  });

  const showAssesmentdetails = async (resume) => {
    const derivedAssessmentId = resume?.AssessmentId || resume?.assessmentId || resume?.assessmentCode;
    const derivedEmail = (resume?.Email || resume?.email || '').toString().trim().toLowerCase();

    if (!derivedAssessmentId || !derivedEmail) {
      alert('Assessment ID or candidate email is missing for this resume.');
      return;
    }

    const headers = { 'Content-Type': 'application/json' };
    if (user?.analyticsToken) {
      headers.Authorization = `Bearer ${user.analyticsToken}`;
    }

    setAssessmentError(null);
    setAssessmentRecord(null);
    setAssessmentModalOpen(true);
    setAssessmentLoading(true);

    try {
      const response = await fetch('https://analytics.insightos.co.uk/api/getassessmentcompleterecord', {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ assessmentId: derivedAssessmentId, email: derivedEmail }),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok || !data?.success) {
        const message = data?.message || 'Unable to load assessment record';
        throw new Error(message);
      }

      setAssessmentRecord(data.data);
    } catch (error) {
      setAssessmentError(error.message || 'Failed to fetch assessment details');
    } finally {
      setAssessmentLoading(false);
    }
  };

  const closeAssessmentModal = () => {
    setAssessmentModalOpen(false);
    setAssessmentRecord(null);
    setAssessmentError(null);
    setAssessmentLoading(false);
  };

  const adminCardClassName = 'rounded-2xl border border-indigo-200/60 bg-gradient-to-r from-indigo-100/70 via-white/90 to-purple-100/70 shadow-[0_12px_30px_rgba(99,102,241,0.2)] backdrop-blur-xl overflow-hidden';
  const getTabButtonClass = (tabKey) =>
    `group relative shrink-0 rounded-xl border px-4 py-2.5 text-xs font-semibold transition-all duration-300 sm:px-5 sm:py-3 sm:text-sm md:text-base ${
      activeTab === tabKey
        ? 'border-white/15 bg-gradient-to-br from-indigo-900/95 to-indigo-700/95 text-white shadow-lg shadow-indigo-950/35 -translate-y-0.5'
        : 'border-indigo-100/80 bg-white/75 text-slate-600 hover:border-indigo-200 hover:bg-white hover:text-indigo-700 hover:shadow-md'
    }`;




  // UI
  return (
    <>
      <StaffNav />
      <div className="bg-gradient-to-br from-indigo-50 via-white to-purple-50 min-h-screen">
        {/* Hero Section */}
        <div className="relative overflow-hidden bg-gradient-to-r from-indigo-900 from-10% via-sky-500 via-30% to-emerald-500 to-90%">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20"></div>
          <div className="relative px-4 py-10 sm:px-6 sm:py-16 lg:py-20 lg:px-8">
            <div className="mx-auto max-w-4xl text-center">
              <h1 className="text-3xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
                Recruitment Hub
              </h1>
              <p className="mt-4 text-lg text-indigo-100 sm:text-xl lg:text-2xl">
                Streamline your hiring process with AI-powered CV scanning and intelligent interview management
              </p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="px-3 py-6 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            {/* Tab Navigation */}
            <div className="mb-6 sm:mb-8">
              <div className="overflow-x-auto pb-1 hide-scrollbar sm:flex sm:justify-center">
              <div className="inline-flex min-w-max gap-1.5 rounded-2xl border border-indigo-100/80 bg-gradient-to-r from-blue-50/95 via-white to-purple-50/95 p-1.5 shadow-[0_12px_30px_rgba(99,102,241,0.12)] sm:mx-auto sm:w-auto sm:max-w-5xl sm:justify-center">
               <button
                  className={getTabButtonClass('assessments')}
                  onClick={() => setActiveTab('assessments')}
                  data-component="recruitment-hub-tabs"
                  data-feature="view_assessments_tab"
                >
                  <span className="relative z-10">🧠 Assessments</span>
                </button>
                <button
                  className={getTabButtonClass('vancancy')}
                  onClick={() => setActiveTab('vancancy')}
                  data-component="recruitment-hub-tabs"
                  data-feature="view_cv_scanner_tab"
                >
                  <span className="relative z-10">🧑‍💼 Vacancies</span>
                </button>
                <button
                  className={getTabButtonClass('scan')}
                  onClick={() => setActiveTab('scan')}
                  data-component="recruitment-hub-tabs"
                  data-feature="view_cv_scanner_tab"
                >
                  <span className="relative z-10">📄 CV Scanner</span>
                </button>
                <button
                  className={getTabButtonClass('shortlist')}
                  onClick={() => setActiveTab('shortlist')}
                  data-component="recruitment-hub-tabs"
                  data-feature="view_shortlist_tab"
                >
                  <span className="relative z-10">⭐ Shortlist</span>
                </button>
                <button
                  className={getTabButtonClass('interviews')}
                  onClick={() => setActiveTab('interviews')}
                  data-component="recruitment-hub-tabs"
                  data-feature="view_interviews_tab"
                >
                  <span className="relative z-10">🎯 Interviews</span>
                </button>
                <button
                  className={getTabButtonClass('onboarding')}
                  onClick={() => setActiveTab('onboarding')}
                  data-component="recruitment-hub-tabs"
                  data-feature="view_onboarding_tab"
                >
                  <span className="relative z-10">✅ Onboarding</span>
                </button>
                
              </div>
              </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'vancancy' && (
              <div className="space-y-8">
                <StaffCosts/>
                </div>)}
            {activeTab === 'scan' && (
              <div className="space-y-8">
                {/* Vacancies Section */}
                <div className={adminCardClassName}>
                  <div className="px-6 py-4 bg-gradient-to-r from-indigo-500 to-purple-600">
                    <h3 className="text-xl font-semibold text-white">Available Vacancies</h3>
                    <p className="text-indigo-100 text-sm mt-1">Select a vacancy to scan CVs against</p>
                  </div>
                  
                  {/* Desktop Table View */}
                  <div className="hidden lg:block overflow-x-auto">
                    <table className="min-w-full border-separate border-spacing-y-2 p-2">
                      <thead>
                        <tr>
                          <th className="px-6 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-indigo-700/80">Role</th>
                          <th className="px-6 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-indigo-700/80">Description</th>
                          <th className="px-6 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-indigo-700/80">Skills</th>
                          <th className="px-6 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-indigo-700/80">Positions</th>
                          <th className="px-6 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-indigo-700/80">Closing Date</th>
                          <th className="px-6 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-indigo-700/80">Status</th>
                          <th className="px-6 py-2 text-right text-[11px] font-semibold uppercase tracking-[0.08em] text-indigo-700/80">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vacancies && vacancies.length > 0 ? (
                          vacancies.map((vacancy) => (
                            <tr 
                              key={vacancy.id} 
                              className={`bg-white/95 ring-1 ring-indigo-100 transition-all hover:ring-indigo-200 ${
                                selectedVacancyId === vacancy.id ? 'ring-2 ring-indigo-300' : ''
                              }`}
                            >
                              <td className="px-6 py-4 font-medium text-gray-900 rounded-l-xl">{vacancy.Role || '-'}</td>
                              <td className="px-6 py-4 text-gray-700 max-w-xs truncate">{vacancy.Description || '-'}</td>
                              <td className="px-6 py-4 text-gray-700 max-w-xs truncate">{vacancy.Skills || '-'}</td>
                              <td className="px-6 py-4 text-gray-700">{vacancy.Jobs || '-'}</td>
                              <td className="px-6 py-4 text-gray-700">
                                {vacancy.ClosingDate
                                  ? (() => {
                                      try {
                                        return format(new Date(vacancy.ClosingDate), 'dd/MM/yyyy');
                                      } catch {
                                        return vacancy.ClosingDate;
                                      }
                                    })()
                                  : '-'}
                              </td>
                              <td className="px-6 py-4">
                                {vacancy.Complete === 1 ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Complete
                                  </span>
                                ) : vacancy.Complete === null ? (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    Incomplete
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                    Unknown
                                  </span>
                                )}
                              </td>
                              <td className="px-6 py-4 text-right rounded-r-xl">
                                <button
                                  className={`inline-flex items-center px-4 py-2 rounded-lg font-semibold text-sm shadow-sm transition-all duration-200 ${
                                    selectedVacancyId === vacancy.id
                                      ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg transform scale-105'
                                      : 'bg-gray-100 text-gray-700 hover:bg-gradient-to-r hover:from-indigo-500 hover:to-purple-600 hover:text-white hover:shadow-lg hover:transform hover:scale-105'
                                  }`}
                                  onClick={() => handleSelectVacancy(vacancy.id)}
                                  data-component="recruitment-vacancies"
                                  data-feature="select_vacancy"
                                  data-vacancy-id={vacancy.id}
                                >
                                  {selectedVacancyId === vacancy.id ? '✓ Selected' : 'Select'}
                                </button>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                              <div className="flex flex-col items-center">
                                <div className="text-4xl mb-2">📋</div>
                                <p className="text-lg font-medium">No vacancies found</p>
                                <p className="text-sm">Create a vacancy to get started</p>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Card View */}
                  <div className="lg:hidden">
                    {vacancies && vacancies.length > 0 ? (
                      <div className="p-4 space-y-4">
                        {vacancies.map((vacancy) => (
                          <div
                            key={vacancy.id}
                            className={`p-4 rounded-xl border-2 transition-all duration-200 ${
                              selectedVacancyId === vacancy.id
                                ? 'border-indigo-500 bg-indigo-50 shadow-lg'
                                : 'border-gray-200 bg-white hover:border-indigo-300 hover:shadow-md'
                            }`}
                          >
                            <div className="flex justify-between items-start mb-3">
                              <h4 className="font-semibold text-gray-900 text-lg">{vacancy.Role || 'Untitled Role'}</h4>
                              <div className="flex items-center space-x-2">
                                {vacancy.Complete === 1 ? (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                    Complete
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                    Incomplete
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            <div className="space-y-2 mb-4">
                              <p className="text-sm text-gray-600">
                                <span className="font-medium">Description:</span> {vacancy.Description || 'No description'}
                              </p>
                              <p className="text-sm text-gray-600">
                                <span className="font-medium">Skills:</span> {vacancy.Skills || 'No skills listed'}
                              </p>
                              <div className="flex justify-between text-sm text-gray-600">
                                <span><span className="font-medium">Positions:</span> {vacancy.Jobs || 0}</span>
                                <span><span className="font-medium">Closes:</span> {
                                  vacancy.ClosingDate
                                    ? (() => {
                                        try {
                                          return format(new Date(vacancy.ClosingDate), 'dd/MM/yyyy');
                                        } catch {
                                          return vacancy.ClosingDate;
                                        }
                                      })()
                                    : 'No date set'
                                }</span>
                              </div>
                            </div>
                            
                            <button
                              className={`w-full py-3 px-4 rounded-lg font-semibold text-sm transition-all duration-200 ${
                                selectedVacancyId === vacancy.id
                                  ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gradient-to-r hover:from-indigo-500 hover:to-purple-600 hover:text-white hover:shadow-lg'
                              }`}
                              onClick={() => handleSelectVacancy(vacancy.id)}
                              data-component="recruitment-vacancies-mobile"
                              data-feature="select_vacancy"
                              data-vacancy-id={vacancy.id}
                            >
                              {selectedVacancyId === vacancy.id ? '✓ Selected' : 'Select This Vacancy'}
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-8 text-center text-gray-500">
                        <div className="flex flex-col items-center">
                          <div className="text-4xl mb-2">📋</div>
                          <p className="text-lg font-medium">No vacancies available</p>
                          <p className="text-sm">Create a vacancy to start scanning CVs</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* File Upload Section */}
                <div className={`${adminCardClassName} p-6 sm:p-8`}>
                  <div className="text-center">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Upload CV for Analysis</h3>
                    <p className="text-gray-600 mb-6">Select a PDF file to analyze against the chosen vacancy</p>
                    
                    {loading && (
                      <div className="mb-6">
                        <LoadingComponent />
                      </div>
                    )}
                    
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={handleFileChange}
                        className="hidden"
                        id="file-upload"
                      />
                      <label
                        htmlFor="file-upload"
                        className="inline-flex items-center gap-x-2 rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 px-6 py-3 text-sm font-semibold text-white shadow-lg hover:shadow-xl hover:from-green-600 hover:to-emerald-700 cursor-pointer transition-all duration-200 transform hover:scale-105"
                        data-component="cv-analyzer"
                        data-feature="select_pdf_file"
                      >
                        📄 Select PDF File
                      </label>
                      <button
                        onClick={handleSubmit}
                        type="button"
                        data-component="cv-analyzer"
                        data-time-saved="30"
                        data-feature="analyze_cv_post"
                        data-vacancy-id={selectedVacancyId || undefined}
                        className="inline-flex items-center gap-x-2 rounded-xl bg-gradient-to-r from-indigo-500 to-purple-600 px-6 py-3 text-sm font-semibold text-white shadow-lg hover:shadow-xl hover:from-indigo-600 hover:to-purple-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed disabled:transform-none transition-all duration-200 transform hover:scale-105"
                        disabled={loading || !file || !selectedVacancyId}
                      >
                        🚀 Analyze CV <CogIcon className="-mr-0.5 h-5 w-5" aria-hidden="true" />
                      </button>
                    </div>
                    
                    <div className="flex flex-col items-center gap-2">
                      {fileName && (
                        <div className="flex items-center gap-2 text-sm text-gray-700 bg-gray-100 px-3 py-2 rounded-lg">
                          <span className="text-green-600">📄</span>
                          <span className="font-medium">Selected:</span>
                          <span>{fileName}</span>
                        </div>
                      )}
                      {!selectedVacancyId && (
                        <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg">
                          <span>⚠️</span>
                          <span>Please select a vacancy first</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* AI Analysis Results */}
                <div className={adminCardClassName}>
                  <div className="px-6 py-4 bg-gradient-to-r from-purple-500 to-indigo-600">
                    <h3 className="text-xl font-semibold text-white">🤖 AI Analysis Results</h3>
                    <p className="text-purple-100 text-sm mt-1">Detailed CV evaluation and recommendations</p>
                  </div>
                  
                  <div className="p-6">
                    {apiResponse && typeof apiResponse === 'object' && apiResponse.score !== null ? (
                      <div className="space-y-6">
                        {/* Action Buttons */}
                        <div className="flex flex-col sm:flex-row justify-end gap-3">
                          <button
                            className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold text-sm shadow-lg hover:shadow-xl hover:from-green-600 hover:to-emerald-700 transition-all duration-200 transform hover:scale-105"
                            onClick={handleShortlist}
                            data-component="cv-analysis-actions"
                            data-feature="shortlist_candidate_post"
                            data-time-saved="10"
                          >
                            ⭐ Shortlist Candidate
                          </button>
                          <button
                            className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-gradient-to-r from-red-500 to-pink-600 text-white font-semibold text-sm shadow-lg hover:shadow-xl hover:from-red-600 hover:to-pink-700 transition-all duration-200 transform hover:scale-105"
                            onClick={handleReject}
                            data-component="cv-analysis-actions"
                            data-feature="reject_candidate_post"
                          >
                            ❌ Reject Candidate
                          </button>
                        </div>

                        {/* Interview Form */}
                        {showInterviewForm && (
                          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                              {/* Left: Form */}
                              <div className="bg-white rounded-xl shadow-lg p-6">
                                <h4 className="text-lg font-semibold mb-4 text-gray-900 flex items-center gap-2">
                                  📝 Interview Details
                                </h4>
                                <div className="space-y-4">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Candidate Name</label>
                                    <input
                                      type="text"
                                      name="name"
                                      value={interviewData.name}
                                      onChange={handleInterviewInputChange}
                                      className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-200"
                                      placeholder="Enter candidate's full name"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                                    <input
                                      type="text"
                                      name="role"
                                      value={interviewData.role}
                                      onChange={handleInterviewInputChange}
                                      className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 bg-gray-50 cursor-not-allowed"
                                      disabled
                                    />
                                  </div>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-2">Interview Date</label>
                                      <input
                                        type="date"
                                        name="date"
                                        value={interviewData.date}
                                        onChange={handleInterviewInputChange}
                                        className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-200"
                                      />
                                    </div>
                                    <div>
                                      <label className="block text-sm font-medium text-gray-700 mb-2">Time</label>
                                      <input
                                        type="time"
                                        name="time"
                                        value={interviewData.time}
                                        onChange={handleInterviewInputChange}
                                        className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-200"
                                      />
                                    </div>
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                                    <input
                                      type="text"
                                      name="location"
                                      value={interviewData.location}
                                      onChange={handleInterviewInputChange}
                                      className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-200"
                                      placeholder="Office address or video call link"
                                    />
                                  </div>
                                  <div className="flex flex-col sm:flex-row gap-3 pt-4">
                                    <button
                                      className="flex-1 inline-flex items-center justify-center px-4 py-3 rounded-lg bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold text-sm shadow-lg hover:shadow-xl hover:from-green-600 hover:to-emerald-700 transition-all duration-200 transform hover:scale-105"
                                      onClick={handleSaveInterview}
                                      data-component="interview-form"
                                      data-time-saved="30"
                                      data-feature="save_interview_post"
                                      data-vacancy-id={selectedVacancyId || undefined}
                                    >
                                      💾 Save Interview
                                    </button>
                                    <button
                                      className="flex-1 inline-flex items-center justify-center px-4 py-3 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold text-sm shadow-lg hover:shadow-xl hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 transform hover:scale-105"
                                      onClick={handleOpenOutlook}
                                      data-component="interview-form"
                                      data-feature="open_outlook_invite"
                                    >
                                      📧 Open in Outlook
                                    </button>
                                  </div>
                                </div>
                              </div>
                              {/* Right: Meeting Calendar */}
                              <div className="bg-white rounded-xl shadow-lg p-6">
                                <h4 className="text-lg font-semibold mb-4 text-gray-900 flex items-center gap-2">
                                  📅 Calendar
                                </h4>
                                <MeetingCalendar />
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Score Display */}
                        <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6 border border-purple-200">
                          <div className="flex flex-col lg:flex-row items-center justify-center gap-8">
                            {/* Score Circle */}
                            <div className="flex flex-col items-center">
                              <h4 className="text-lg font-semibold text-gray-900 mb-4">Overall Score</h4>
                              <div className="relative">
                                <svg width="120" height="120" viewBox="0 0 120 120" className="transform -rotate-90">
                                  <circle
                                    cx="60"
                                    cy="60"
                                    r="45"
                                    fill="none"
                                    stroke="#e5e7eb"
                                    strokeWidth="8"
                                  />
                                  <circle
                                    cx="60"
                                    cy="60"
                                    r="45"
                                    fill="none"
                                    stroke={
                                      apiResponse.score <= 3
                                        ? "#ef4444"
                                        : apiResponse.score <= 5
                                        ? "#fb923c"
                                        : apiResponse.score <= 8
                                        ? "#facc15"
                                        : "#22c55e"
                                    }
                                    strokeWidth="8"
                                    strokeDasharray={2 * Math.PI * 45}
                                    strokeDashoffset={
                                      2 * Math.PI * 45 * (1 - Math.min(apiResponse.score, 10) / 10)
                                    }
                                    strokeLinecap="round"
                                    className="transition-all duration-1000 ease-out"
                                  />
                                </svg>
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <div className="text-center">
                                    <div className="text-3xl font-bold text-gray-900">{apiResponse.score}</div>
                                    <div className="text-sm text-gray-600">out of 100</div>
                                  </div>
                                </div>
                              </div>
                              <div className="mt-2 text-center">
                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                                  apiResponse.score <= 30
                                    ? 'bg-red-100 text-red-800'
                                    : apiResponse.score <= 50
                                    ? 'bg-orange-100 text-orange-800'
                                    : apiResponse.score <= 80
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-green-100 text-green-800'
                                }`}>
                                  {apiResponse.score <= 30 ? '🔴 Poor Match' : 
                                   apiResponse.score <= 50 ? '🟡 Fair Match' :
                                   apiResponse.score <= 80 ? '🟠 Good Match' : '🟢 Excellent Match'}
                                </span>
                              </div>
                            </div>
                            
                            {/* Feedback */}
                            <div className="flex-1 max-w-2xl">
                              <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                🎯 Detailed Feedback
                              </h4>
                              <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                                <div className="text-gray-800 text-sm leading-relaxed whitespace-pre-wrap">
                                  {apiResponse.feedback}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <div className="text-6xl mb-4">🤖</div>
                        <h4 className="text-xl font-semibold text-gray-900 mb-2">Ready to Analyze</h4>
                        <p className="text-gray-600">Upload a PDF and select a vacancy to get started with AI-powered CV analysis</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Shortlist Tab */}
            {activeTab === 'shortlist' && (
              <div className="space-y-8">
                {/* Interview Form Modal */}
                {showInterviewForm && (
                  <div className={adminCardClassName}>
                    <div className="px-6 py-4 bg-gradient-to-r from-blue-500 to-indigo-600">
                      <h3 className="text-xl font-semibold text-white">📅 Schedule Interview</h3>
                      <p className="text-blue-100 text-sm mt-1">Fill in the interview details</p>
                    </div>
                    <div className="p-6">
                      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          {/* Left: Form */}
                          <div className="bg-white rounded-xl shadow-lg p-6">
                            <h4 className="text-lg font-semibold mb-4 text-gray-900 flex items-center gap-2">
                              📝 Interview Details
                            </h4>
                            <div className="space-y-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Candidate Name</label>
                                <input
                                  type="text"
                                  name="name"
                                  value={interviewData.name}
                                  onChange={handleInterviewInputChange}
                                  className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-200"
                                  placeholder="Enter candidate's full name"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                                <input
                                  type="text"
                                  name="role"
                                  value={interviewData.role}
                                  onChange={handleInterviewInputChange}
                                  className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 bg-gray-50 cursor-not-allowed"
                                  disabled
                                />
                              </div>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">Interview Date</label>
                                  <input
                                    type="date"
                                    name="date"
                                    value={interviewData.date}
                                    onChange={handleInterviewInputChange}
                                    className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-200"
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">Time</label>
                                  <input
                                    type="time"
                                    name="time"
                                    value={interviewData.time}
                                    onChange={handleInterviewInputChange}
                                    className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-200"
                                  />
                                </div>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Location</label>
                                <input
                                  type="text"
                                  name="location"
                                  value={interviewData.location}
                                  onChange={handleInterviewInputChange}
                                  className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-200"
                                  placeholder="Office address or video call link"
                                />
                              </div>
                              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                                <button
                                  className="flex-1 inline-flex items-center justify-center px-4 py-3 rounded-lg bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold text-sm shadow-lg hover:shadow-xl hover:from-green-600 hover:to-emerald-700 transition-all duration-200 transform hover:scale-105"
                                  onClick={handleSaveInterview}
                                  data-component="interview-form-shortlist"
                                  data-time-saved="30"
                                  data-feature="save_interview_post"
                                  data-vacancy-id={selectedVacancyId || undefined}
                                >
                                  💾 Save Interview
                                </button>
                                <button
                                  className="flex-1 inline-flex items-center justify-center px-4 py-3 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-semibold text-sm shadow-lg hover:shadow-xl hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 transform hover:scale-105"
                                  onClick={handleOpenOutlook}
                                  data-component="interview-form-shortlist"
                                  data-feature="open_outlook_invite"
                                >
                                  📧 Open in Outlook
                                </button>
                                <button
                                  className="flex-1 inline-flex items-center justify-center px-4 py-3 rounded-lg bg-gradient-to-r from-gray-500 to-gray-600 text-white font-semibold text-sm shadow-lg hover:shadow-xl hover:from-gray-600 hover:to-gray-700 transition-all duration-200 transform hover:scale-105"
                                  onClick={() => setShowInterviewForm(false)}
                                  data-component="interview-form-shortlist"
                                  data-feature="cancel_interview_form"
                                >
                                  ❌ Cancel
                                </button>
                              </div>
                            </div>
                          </div>
                          {/* Right: Meeting Calendar */}
                          <div className="bg-white rounded-xl shadow-lg p-6">
                            <h4 className="text-lg font-semibold mb-4 text-gray-900 flex items-center gap-2">
                              📅 Calendar
                            </h4>
                            <MeetingCalendar />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className={adminCardClassName}>
                  <div className="px-6 py-4 bg-gradient-to-r from-green-500 to-emerald-600">
                    <h3 className="text-xl font-semibold text-white">⭐ Shortlisted Candidates</h3>
                    <p className="text-green-100 text-sm mt-1">Review and manage your shortlisted candidates</p>
                  </div>

                  {loadingShortlist ? (
                    <div className="p-8">
                      <LoadingComponent />
                    </div>
                  ) : Object.keys(resumesByVacancy).length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      <p className="text-lg">No shortlisted candidates yet</p>
                      <p className="text-sm mt-2">Scan CVs and shortlist them to see them here</p>
                    </div>
                  ) : (
                    <div className="p-4 md:p-6 space-y-6 md:space-y-8">
                      {Object.entries(resumesByVacancy).map(([vacancyId, resumes]) => {
                        const vacancy = resumes[0]?.vacancy;
                        return (
                          <div key={vacancyId} className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                            <div className="bg-gradient-to-r from-indigo-100 to-purple-100 px-4 py-3">
                              <h4 className="text-base md:text-lg font-semibold text-indigo-900">{vacancy?.Role || 'Unknown Role'}</h4>
                              <p className="text-xs md:text-sm text-indigo-700">{resumes.length} candidate{resumes.length !== 1 ? 's' : ''}</p>
                            </div>
                            
                            {/* Desktop Table View */}
                            <div className="hidden lg:block overflow-x-auto">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Name</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Email</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Phone</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Location</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">CV Score</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Assessment Score</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Final Score</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Status</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">Actions</th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {resumes.map((resume) => (
                                    <React.Fragment key={resume.id}>
                                      <tr className="hover:bg-gray-50">
                                        <td className="px-4 py-4 text-sm text-gray-900 font-medium">{resume.CandidateName || 'N/A'}</td>
                                        <td className="px-4 py-4 text-sm text-gray-900">{resume.Email || 'N/A'}</td>
                                        <td className="px-4 py-4 text-sm text-gray-900">{resume.Telephone || 'N/A'}</td>
                                        <td className="px-4 py-4 text-sm text-gray-900">{resume.Location || 'N/A'}</td>
                                        <td className="px-4 py-4 text-sm">
                                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                            resume.Score >= 75 ? 'bg-green-100 text-green-800' :
                                            resume.Score >= 50 ? 'bg-yellow-100 text-yellow-800' :
                                            'bg-red-100 text-red-800'
                                          }`}>
                                            {resume.Score}/100
                                          </span>
                                        </td>
                                        <td className="px-4 py-4 text-sm">
                                          {resume.CompleteAssessment === 1 ? (
                                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                              {resume.FinalScore || 0}/30
                                            </span>
                                          ) : resume.AssessmentSent === 1 ? (
                                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                              ⏳ Pending
                                            </span>
                                          ) : (
                                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                              ➖ Not Sent
                                            </span>
                                          )}
                                        </td>
                                        <td className="px-4 py-4 text-sm">
                                          {(() => {
                                            const cvScore = resume.Score || 0;
                                            const assessmentScore = resume.CompleteAssessment === 1 ? (resume.FinalScore || 0) * 10 : 0;
                                            const finalScore = cvScore + assessmentScore;
                                            return (
                                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                                finalScore >= 150 ? 'bg-green-100 text-green-800' :
                                                finalScore >= 100 ? 'bg-yellow-100 text-yellow-800' :
                                                'bg-red-100 text-red-800'
                                              }`}>
                                                {finalScore}/400
                                              </span>
                                            );
                                          })()}
                                        </td>
                                        <td className="px-4 py-4 text-sm">
                                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                            resume.Status === 'Interview' ? 'bg-blue-100 text-blue-800' :
                                            'bg-green-100 text-green-800'
                                          }`}>
                                            {resume.Status || 'Shortlist'}
                                          </span>
                                        </td>
                                        <td className="px-4 py-4 text-sm">
                                          <div className="flex flex-wrap gap-2">
                                            <button
                                              onClick={() => handleToggleCV(resume.id)}
                                              className="inline-flex items-center px-3 py-1.5 rounded-lg bg-gradient-to-r from-purple-500 to-indigo-600 text-white text-xs font-semibold hover:from-purple-600 hover:to-indigo-700 transition-all"
                                            >
                                              {expandedCVId === resume.id ? '📄 Collapse CV' : '📄 Expand CV'}
                                            </button>
                                            <button
                                              onClick={() => handleScheduleInterviewFromShortlist(resume)}
                                              className="inline-flex items-center px-3 py-1.5 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-xs font-semibold hover:from-blue-600 hover:to-indigo-700 transition-all"
                                            >
                                              📅 Schedule
                                            </button>
                                            {resume.CompleteAssessment === 1 && (
                                              <button
                                                onClick={() => showAssesmentdetails(resume)}
                                                disabled={assessmentLoading}
                                                className="inline-flex items-center px-3 py-1.5 rounded-lg bg-gradient-to-r from-green-500 to-teal-600 text-white text-xs font-semibold hover:from-green-600 hover:to-teal-700 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                                              >
                                                {assessmentLoading ? '⏳ Loading...' : '📊 View Assessment'}
                                              </button>
                                            )}
                                            <button
                                            onClick={() => handleSendAssessmentFromShortlist(resume)}
                                            className="inline-flex items-center px-3 py-1.5 rounded-lg bg-gradient-to-r from-green-500 to-teal-600 text-white text-xs font-semibold hover:from-green-600 hover:to-teal-700 transition-all"
                                            >
                                              {resume.AssessmentSent === 1 ? '✅ Assessment Sent' : '📝 Send Assessment'}
                                            </button>
                                            <button
                                              onClick={() => handleRejectFromShortlist(resume.id)}
                                              className="inline-flex items-center px-3 py-1.5 rounded-lg bg-gradient-to-r from-red-500 to-pink-600 text-white text-xs font-semibold hover:from-red-600 hover:to-pink-700 transition-all"
                                            >
                                              ❌ Reject
                                            </button>
                                          </div>
                                        </td>
                                      </tr>
                                      {expandedCVId === resume.id && (
                                        <tr>
                                          <td colSpan={10} className="px-4 py-6 bg-gradient-to-br from-gray-50 to-indigo-50">
                                            <div className="max-w-5xl mx-auto">
                                              {/* Mock CV Layout */}
                                              <div className="bg-white rounded-lg shadow-lg p-6 md:p-8 border border-gray-200">
                                                {/* CV Header */}
                                                <div className="border-b-2 border-indigo-500 pb-4 mb-6">
                                                  <h3 className="text-2xl md:text-3xl font-bold text-gray-900">{resume.CandidateName || 'Candidate Name'}</h3>
                                                  <div className="mt-2 flex flex-wrap gap-4 text-sm text-gray-600">
                                                    <span className="flex items-center gap-1">📧 {resume.Email || 'N/A'}</span>
                                                    <span className="flex items-center gap-1">📱 {resume.Telephone || 'N/A'}</span>
                                                    <span className="flex items-center gap-1">📍 {resume.Location || 'N/A'}</span>
                                                  </div>
                                                </div>

                                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                                  {/* Left Sidebar - Qualifications */}
                                                  <div className="md:col-span-1 space-y-4">
                                                    <div className="bg-indigo-50 rounded-lg p-4">
                                                      <h4 className="text-lg font-semibold text-indigo-900 mb-3 flex items-center gap-2">
                                                        🎓 Qualifications
                                                      </h4>
                                                      <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                                                        {resume.Qualifications || 'No qualifications provided'}
                                                      </div>
                                                    </div>

                                                    <div className="bg-green-50 rounded-lg p-4">
                                                      <h4 className="text-lg font-semibold text-green-900 mb-3 flex items-center gap-2">
                                                        ⭐ AI Score
                                                      </h4>
                                                      <div className="text-center">
                                                        <div className={`text-3xl font-bold ${
                                                          resume.Score >= 75 ? 'text-green-600' :
                                                          resume.Score >= 50 ? 'text-yellow-600' :
                                                          'text-red-600'
                                                        }`}>
                                                          {resume.Score}/100
                                                        </div>
                                                      </div>
                                                    </div>

                                                    <div className="bg-blue-50 rounded-lg p-4">
                                                      <h4 className="text-lg font-semibold text-blue-900 mb-3 flex items-center gap-2">
                                                        💬 AI Feedback
                                                      </h4>
                                                      <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                                                        {resume.Feedback || 'No feedback provided'}
                                                      </div>
                                                    </div>
                                                  </div>

                                                  {/* Main Content - About & Experience */}
                                                  <div className="md:col-span-2 space-y-6">
                                                    <div>
                                                      <h4 className="text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2 border-b-2 border-gray-200 pb-2">
                                                        👤 About
                                                      </h4>
                                                      <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                                                        {resume.About || 'No information provided'}
                                                      </div>
                                                    </div>

                                                    <div>
                                                      <h4 className="text-xl font-semibold text-gray-900 mb-3 flex items-center gap-2 border-b-2 border-gray-200 pb-2">
                                                        💼 Experience
                                                      </h4>
                                                      <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                                                        {resume.Experience || 'No experience provided'}
                                                      </div>
                                                    </div>
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          </td>
                                        </tr>
                                      )}
                                    </React.Fragment>
                                  ))}
                                </tbody>
                              </table>
                            </div>

                            {/* Mobile Card View */}
                            <div className="lg:hidden divide-y divide-gray-200">
                              {resumes.map((resume) => (
                                <div key={resume.id} className="p-4 hover:bg-gray-50 transition-colors">
                                  <div className="space-y-3">
                                    {/* Header */}
                                    <div className="flex justify-between items-start">
                                      <div className="flex-1">
                                        <h5 className="font-semibold text-gray-900 text-base">{resume.CandidateName || 'N/A'}</h5>
                                        <p className="text-xs text-gray-600 mt-1">{resume.Email || 'N/A'}</p>
                                      </div>
                                      <div className="text-right space-y-1">
                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                          resume.Score >= 75 ? 'bg-green-100 text-green-800' :
                                          resume.Score >= 50 ? 'bg-yellow-100 text-yellow-800' :
                                          'bg-red-100 text-red-800'
                                        }`}>
                                          CV: {resume.Score}/100
                                        </span>
                                        {(() => {
                                          const cvScore = resume.Score || 0;
                                          const assessmentScore = resume.CompleteAssessment === 1 ? (resume.FinalScore || 0) * 10 : 0;
                                          const finalScore = cvScore + assessmentScore;
                                          return (
                                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                              finalScore >= 150 ? 'bg-purple-100 text-purple-800' :
                                              finalScore >= 100 ? 'bg-blue-100 text-blue-800' :
                                              'bg-gray-100 text-gray-800'
                                            }`}>
                                              Total: {finalScore}/400
                                            </span>
                                          );
                                        })()}
                                      </div>
                                    </div>

                                    {/* Details Grid */}
                                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                                      <div>
                                        <span className="font-medium">Phone:</span> {resume.Telephone || 'N/A'}
                                      </div>
                                      <div>
                                        <span className="font-medium">Location:</span> {resume.Location || 'N/A'}
                                      </div>
                                      <div className="col-span-2">
                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                          resume.Status === 'Interview' ? 'bg-blue-100 text-blue-800' :
                                          'bg-green-100 text-green-800'
                                        }`}>
                                          Status: {resume.Status || 'Shortlist'}
                                        </span>
                                      </div>
                                      <div className="col-span-2">
                                        <span className="font-medium">Assessment:</span>
                                        {resume.CompleteAssessment === 1 ? (
                                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 ml-2">
                                            ✅ {resume.FinalScore || 0}/10
                                            </span>
                                            
                                        ) : resume.AssessmentSent === 1 ? (
                                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                            ⏳ Assessment Sent
                                          </span>
                                        ) : (
                                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                            ➖ Not Sent
                                          </span>
                                        )}
                                      </div>
                                    </div>

                                    {/* Expanded CV View Mobile */}
                                    {expandedCVId === resume.id && (
                                      <div className="mt-4 bg-gradient-to-br from-gray-50 to-indigo-50 rounded-lg p-4 border border-gray-200">
                                        <div className="space-y-4">
                                          {/* About Section */}
                                          <div>
                                            <h6 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-1">
                                              👤 About
                                            </h6>
                                            <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap">
                                              {resume.About || 'No information provided'}
                                            </p>
                                          </div>

                                          {/* Qualifications */}
                                          <div className="bg-indigo-50 rounded-lg p-3">
                                            <h6 className="text-sm font-semibold text-indigo-900 mb-2 flex items-center gap-1">
                                              🎓 Qualifications
                                            </h6>
                                            <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap">
                                              {resume.Qualifications || 'No qualifications provided'}
                                            </p>
                                          </div>

                                          {/* Experience */}
                                          <div>
                                            <h6 className="text-sm font-semibold text-gray-900 mb-2 flex items-center gap-1">
                                              💼 Experience
                                            </h6>
                                            <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap">
                                              {resume.Experience || 'No experience provided'}
                                            </p>
                                          </div>

                                          {/* AI Feedback */}
                                          <div className="bg-blue-50 rounded-lg p-3">
                                            <h6 className="text-sm font-semibold text-blue-900 mb-2 flex items-center gap-1">
                                              💬 AI Feedback
                                            </h6>
                                            <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-wrap">
                                              {resume.Feedback || 'No feedback provided'}
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                    )}

                                    {/* Action Buttons */}
                                    <div className="flex flex-col gap-2 pt-2">
                                      <button
                                        onClick={() => handleToggleCV(resume.id)}
                                        className="w-full inline-flex items-center justify-center px-4 py-2 rounded-lg bg-gradient-to-r from-purple-500 to-indigo-600 text-white text-sm font-semibold hover:from-purple-600 hover:to-indigo-700 transition-all"
                                      >
                                        {expandedCVId === resume.id ? '📄 Collapse CV' : '📄 Expand CV'}
                                      </button>
                                      <div className="grid grid-cols-2 gap-2">
                                        <button
                                          onClick={() => handleScheduleInterviewFromShortlist(resume)}
                                          className="inline-flex items-center justify-center px-3 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-sm font-semibold hover:from-blue-600 hover:to-indigo-700 transition-all"
                                        >
                                          📅 Schedule
                                        </button>
                                        <button
                                          onClick={() => handleRejectFromShortlist(resume.id)}
                                          className="inline-flex items-center justify-center px-3 py-2 rounded-lg bg-gradient-to-r from-red-500 to-pink-600 text-white text-sm font-semibold hover:from-red-600 hover:to-pink-700 transition-all"
                                        >
                                          ❌ Reject
                                        </button>
                                      </div>
                                      {resume.CompleteAssessment === 1 && (
                                        <button
                                          onClick={() => showAssesmentdetails(resume)}
                                          disabled={assessmentLoading}
                                          className="w-full inline-flex items-center justify-center px-4 py-2 rounded-lg bg-gradient-to-r from-green-500 to-teal-600 text-white text-sm font-semibold hover:from-green-600 hover:to-teal-700 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                                        >
                                          {assessmentLoading ? '⏳ Loading...' : '📊 View Assessment'}
                                        </button>
                                      )}
                                      <button
                                        onClick={() => handleSendAssessmentFromShortlist(resume)}
                                        className="w-full inline-flex items-center justify-center px-4 py-2 rounded-lg bg-gradient-to-r from-green-500 to-teal-600 text-white text-sm font-semibold hover:from-green-600 hover:to-teal-700 transition-all"
                                      >
                                        {resume.AssessmentSent === 1 ? '✅ Assessment Sent' : '📝 Send Assessment'}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Interviews Tab */}
            {activeTab === 'interviews' && (
              <div className={adminCardClassName}>
                <div className="px-6 py-4 bg-gradient-to-r from-purple-500 to-indigo-600">
                  <h3 className="text-xl font-semibold text-white">🎯 Interview Management</h3>
                  <p className="text-purple-100 text-sm mt-1">Manage scheduled interviews and track progress</p>
                </div>
                
                <div className="p-6">
                  {loadingInterviews ? (
                    <div className="flex justify-center py-12">
                      <LoadingComponent />
                    </div>
                  ) : vacancies && vacancies.length > 0 ? (
                    <div className="space-y-6">
                      {/* Desktop View */}
                      <div className="hidden md:block">
                        <div className="overflow-x-auto">
                          <table className="min-w-full border-separate border-spacing-y-2 p-2">
                            <thead>
                              <tr>
                                <th className="px-6 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-indigo-700/80">Vacancy</th>
                                <th className="px-6 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-indigo-700/80">Description</th>
                                <th className="px-6 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-indigo-700/80">Skills</th>
                                <th className="px-6 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-indigo-700/80">Positions</th>
                                <th className="px-6 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-indigo-700/80">Status</th>
                                <th className="px-6 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-indigo-700/80">Interviews</th>
                                <th className="px-6 py-2 text-right text-[11px] font-semibold uppercase tracking-[0.08em] text-indigo-700/80">Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {vacancies.map((vacancy) => (
                                <React.Fragment key={vacancy.id}>
                                  <tr className="bg-white/95 ring-1 ring-indigo-100 transition-all hover:ring-indigo-200">
                                    <td className="px-6 py-4 font-medium text-gray-900 rounded-l-xl">{vacancy.Role || '-'}</td>
                                    <td className="px-6 py-4 text-gray-700 max-w-xs truncate">{vacancy.Description || '-'}</td>
                                    <td className="px-6 py-4 text-gray-700 max-w-xs truncate">{vacancy.Skills || '-'}</td>
                                    <td className="px-6 py-4 text-gray-700">{vacancy.Jobs || '-'}</td>
                                    <td className="px-6 py-4">
                                      {vacancy.Complete ? (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                          Complete
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                          Incomplete
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-6 py-4 text-gray-700">
                                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                        {vacancy.Interviews} Interviews
                                      </span>
                                    </td>
                                    <td className="px-6 py-4 text-right rounded-r-xl">
                                      <button
                                        className={`inline-flex items-center px-4 py-2 rounded-lg font-semibold text-sm shadow-sm transition-all duration-200 ${
                                          expandedVacancyIds[vacancy.id]
                                            ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg'
                                            : 'bg-gray-100 text-gray-700 hover:bg-gradient-to-r hover:from-indigo-500 hover:to-purple-600 hover:text-white hover:shadow-lg'
                                        }`}
                                        onClick={() => handleToggleVacancyExpand(vacancy.id)}
                                        data-component="interview-management"
                                        data-feature="toggle_vacancy_interviews"
                                        data-vacancy-id={vacancy.id}
                                      >
                                        {expandedVacancyIds[vacancy.id] ? 'Hide' : 'Show'} Interviews
                                      </button>
                                    </td>
                                  </tr>
                                  {expandedVacancyIds[vacancy.id] && (
                                    <tr>
                                      <td colSpan={7} className="bg-indigo-50/50 px-6 py-6">
                                        {interviews.filter(iv => iv.VacancyId === vacancy.id).length > 0 ? (
                                          <div className="space-y-4">
                                            {interviews
                                              .filter(iv => iv.VacancyId === vacancy.id)
                                              .map((interview, idx) => (
                                                <div key={interview.id || idx} className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                                                  {/* Interview Header */}
                                                  <div className="flex justify-between items-start mb-4">
                                                    <div>
                                                      <h5 className="text-lg font-semibold text-gray-900">{interview.Name || 'Unnamed Candidate'}</h5>
                                                      <p className="text-sm text-gray-600 mt-1">{interview.Role || vacancy.Role}</p>
                                                    </div>
                                                    <div className="flex items-center space-x-2">
                                                      {interview.Passed === 1 ? (
                                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                          ✓ Passed
                                                        </span>
                                                      ) : interview.Passed === 0 ? (
                                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                          ✗ Failed
                                                        </span>
                                                      ) : (
                                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                                          ⏳ Pending
                                                        </span>
                                                      )}
                                                    </div>
                                                  </div>

                                                  {/* Interview Details */}
                                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 pb-4 border-b border-gray-200">
                                                    <div>
                                                      <p className="text-xs font-medium text-gray-500 mb-1">Date</p>
                                                      <p className="text-sm text-gray-900">
                                                        {interview.Date
                                                          ? (() => {
                                                              try {
                                                                return format(new Date(interview.Date), 'dd/MM/yyyy');
                                                              } catch {
                                                                return interview.Date;
                                                              }
                                                            })()
                                                          : 'Not set'}
                                                      </p>
                                                    </div>
                                                    <div>
                                                      <p className="text-xs font-medium text-gray-500 mb-1">Time</p>
                                                      <p className="text-sm text-gray-900">{interview.Time || 'Not set'}</p>
                                                    </div>
                                                    <div className="col-span-2">
                                                      <p className="text-xs font-medium text-gray-500 mb-1">Location</p>
                                                      <p className="text-sm text-gray-900">{interview.Location || 'Not specified'}</p>
                                                    </div>
                                                    {interview.TotalScore !== undefined && (
                                                      <div className="col-span-2 md:col-span-4">
                                                        <p className="text-xs font-medium text-gray-500 mb-1">Total Score</p>
                                                        <p className="text-sm text-gray-900 font-semibold">{interview.TotalScore}/100</p>
                                                      </div>
                                                    )}
                                                  </div>

                                                  {/* Action Buttons */}
                                                  <div className="flex flex-wrap gap-2 mb-4">
                                                    {interview.Passed === null && (
                                                      <button
                                                        className="inline-flex items-center px-4 py-2 rounded-lg bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-medium text-sm shadow-sm hover:shadow-lg transition-all duration-200"
                                                        onClick={() => openDecisionModal(interview)}
                                                        data-component="desktop-interview-card"
                                                        data-feature="open_decision_modal"
                                                        data-time-saved="30"
                                                        data-interview-id={interview.id}
                                                      >
                                                        📝 Make Decision
                                                      </button>
                                                    )}
                                                    {interview.Passed === 1 && interview.Accepted === null && (
                                                      <>
                                                        <button
                                                          className="inline-flex items-center px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-green-600 text-white font-medium text-sm shadow-sm hover:shadow-lg transition-all duration-200"
                                                          onClick={() => handleCandidateAccepted(interview.id)}
                                                          data-component="desktop-interview-card"
                                                          data-feature="accept_candidate_post"
                                                          data-time-saved="30"
                                                          data-interview-id={interview.id}
                                                        >
                                                          ✅ Accept Candidate
                                                        </button>
                                                        <button
                                                          className="inline-flex items-center px-4 py-2 rounded-lg bg-gradient-to-r from-gray-500 to-gray-600 text-white font-medium text-sm shadow-sm hover:shadow-lg transition-all duration-200"
                                                          onClick={() => handleCandidateRejected(interview.id)}
                                                          data-component="desktop-interview-card"
                                                          data-feature="reject_candidate_post"
                                                          data-time-saved="30"
                                                          data-interview-id={interview.id}
                                                        >
                                                          ❌ Reject Candidate
                                                        </button>
                                                      </>
                                                    )}
                                                    {interview.Complete !== 1 && (
                                                      <button
                                                        onClick={() => handleDeleteInterview(interview.id)}
                                                        data-component="desktop-interview-card"
                                                        data-feature="cancel_interview_delete"
                                                        data-interview-id={interview.id}
                                                        className="inline-flex items-center px-4 py-2 rounded-lg bg-gradient-to-r from-red-500 to-pink-600 text-white font-medium text-sm shadow-sm hover:shadow-lg transition-all duration-200"
                                                      >
                                                        🗑️ Cancel Interview
                                                      </button>
                                                    )}
                                                    {(!interview.questions || interview.questions.length === 0) ? (
                                                      <button
                                                        onClick={() => handleQuestionGeneration(interview.id)}
                                                        disabled={generatingQuestions[interview.id]}
                                                        data-component="desktop-interview-card"
                                                        data-feature="generate_questions_post"
                                                        data-time-saved="60"
                                                        data-interview-id={interview.id}
                                                        className="inline-flex items-center px-4 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-green-600 text-white font-medium text-sm shadow-sm hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                                      >
                                                        {generatingQuestions[interview.id] ? (
                                                          <>
                                                            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                            Generating...
                                                          </>
                                                        ) : (
                                                          '🤖 Generate Questions'
                                                        )}
                                                      </button>
                                                    ) : (
                                                      <button
                                                        onClick={() => handleToggleShowQuestions(interview.id)}
                                                        data-component="desktop-interview-card"
                                                        data-feature="toggle_question_list"
                                                        data-interview-id={interview.id}
                                                        className="inline-flex items-center px-4 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-medium text-sm shadow-sm hover:shadow-lg transition-all duration-200"
                                                      >
                                                        {showQuestions[interview.id] ? '👆 Hide Questions' : '👇 Show Questions'}
                                                      </button>
                                                    )}
                                                  </div>

                                                  {/* Questions Section */}
                                                  {showQuestions[interview.id] && interview.questions && interview.questions.length > 0 && (
                                                    <div className="mt-4 pt-4 border-t border-gray-200">
                                                      <h6 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                                        ❓ Interview Questions ({interview.questions.length})
                                                      </h6>
                                                      <div className="space-y-3">
                                                        {interview.questions.map((question, qIdx) => (
                                                          <div key={question.id || qIdx} className="bg-gray-50 rounded-lg p-4">
                                                            {editingQuestionId[interview.id] === question.id ? (
                                                              <div className="space-y-3">
                                                                <textarea
                                                                  className="w-full border-2 border-indigo-300 rounded-lg px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                                                                  rows={3}
                                                                  value={editingQuestionText[interview.id] || ''}
                                                                  onChange={(e) => handleChangeEditingQuestionText(interview.id, e.target.value)}
                                                                />
                                                                <div className="flex gap-2">
                                                                  <button
                                                                    className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                                                                    onClick={() => handleSaveQuestion(interview.id, question)}
                                                                    data-component="desktop-question-edit"
                                                                    data-feature="save_question_post"
                                                                    data-interview-id={interview.id}
                                                                    data-question-id={question.id || qIdx}
                                                                  >
                                                                    💾 Save
                                                                  </button>
                                                                  <button
                                                                    className="px-3 py-1.5 bg-gray-500 text-white text-sm rounded-lg hover:bg-gray-600 transition-colors"
                                                                    onClick={() => handleCancelEditQuestion(interview.id)}
                                                                    data-component="desktop-question-edit"
                                                                    data-feature="cancel_question_edit"
                                                                  >
                                                                    ❌ Cancel
                                                                  </button>
                                                                </div>
                                                              </div>
                                                            ) : (
                                                              <>
                                                                <div className="flex justify-between items-start mb-2">
                                                                  <span className="text-xs font-semibold text-gray-500">Question {qIdx + 1}</span>
                                                                  {question.score !== null && question.score !== undefined && (
                                                                    <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                                                                      question.score <= 3 ? 'bg-red-100 text-red-800' :
                                                                      question.score <= 5 ? 'bg-orange-100 text-orange-800' :
                                                                      question.score <= 8 ? 'bg-yellow-100 text-yellow-800' :
                                                                      'bg-green-100 text-green-800'
                                                                    }`}>
                                                                      {question.score}/10
                                                                    </span>
                                                                  )}
                                                                </div>
                                                                <p className="text-sm text-gray-800 mb-3 font-medium">{question.question || question.Question}</p>
                                                                {(question.answer || question.Answer) && (
                                                                  <div className="bg-white rounded p-3 mb-3">
                                                                    <p className="text-xs font-medium text-gray-500 mb-1">Answer:</p>
                                                                    <p className="text-sm text-gray-700">{question.answer || question.Answer}</p>
                                                                  </div>
                                                                )}
                                                                <div className="flex gap-2">
                                                                  <button
                                                                    className="px-3 py-1.5 bg-indigo-100 text-indigo-800 text-sm rounded-lg hover:bg-indigo-200 transition-colors font-medium"
                                                                    onClick={() => handleEditQuestion(interview.id, question)}
                                                                    data-component="desktop-question-list"
                                                                    data-feature="edit_question_toggle"
                                                                    data-interview-id={interview.id}
                                                                    data-question-id={question.id || qIdx}
                                                                  >
                                                                    ✏️ Edit
                                                                  </button>
                                                                  <button
                                                                    className="px-3 py-1.5 bg-red-100 text-red-800 text-sm rounded-lg hover:bg-red-200 transition-colors font-medium"
                                                                    onClick={() => handleDeleteQuestion(interview.id, question)}
                                                                    data-component="desktop-question-list"
                                                                    data-feature="delete_question_delete"
                                                                    data-interview-id={interview.id}
                                                                    data-question-id={question.id || qIdx}
                                                                  >
                                                                    🗑️ Delete
                                                                  </button>
                                                                </div>
                                                              </>
                                                            )}
                                                          </div>
                                                        ))}
                                                      </div>
                                                    </div>
                                                  )}
                                                </div>
                                              ))}
                                          </div>
                                        ) : (
                                          <div className="text-center text-gray-500 py-4">
                                            No interviews scheduled for this vacancy
                                          </div>
                                        )}
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Mobile/Tablet Card View */}
                      <div className="md:hidden space-y-4">
                        {vacancies.map((vacancy) => (
                          <div key={vacancy.id} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                            <div className="p-4 border-b border-gray-100">
                              <div className="flex justify-between items-start mb-3">
                                <h4 className="font-semibold text-gray-900 text-lg">{vacancy.Role || 'Untitled Role'}</h4>
                                <div className="flex items-center space-x-2">
                                  {vacancy.Complete ? (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                      Complete
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                      Incomplete
                                    </span>
                                  )}
                                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    {vacancy.Interviews || 0} interviews
                                  </span>
                                </div>
                              </div>
                              
                              <div className="space-y-2 mb-4">
                                <p className="text-sm text-gray-600">
                                  <span className="font-medium">Description:</span> {vacancy.Description || 'No description'}
                                </p>
                                <p className="text-sm text-gray-600">
                                  <span className="font-medium">Skills:</span> {vacancy.Skills || 'No skills listed'}
                                </p>
                                <div className="flex justify-between text-sm text-gray-600">
                                  <span><span className="font-medium">Positions:</span> {vacancy.Jobs || 0}</span>
                                  <span><span className="font-medium">Closes:</span> {
                                    vacancy.ClosingDate
                                      ? (() => {
                                          try {
                                            return format(new Date(vacancy.ClosingDate), 'dd/MM/yyyy');
                                          } catch {
                                            return vacancy.ClosingDate;
                                          }
                                        })()
                                      : 'No date set'
                                  }</span>
                                </div>
                              </div>
                              
                              <button
                                className={`w-full py-3 px-4 rounded-lg font-semibold text-sm transition-all duration-200 ${
                                  expandedVacancyIds[vacancy.id]
                                    ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gradient-to-r hover:from-indigo-500 hover:to-purple-600 hover:text-white hover:shadow-lg'
                                }`}
                                onClick={() => handleToggleVacancyExpand(vacancy.id)}
                                data-component="interview-management-mobile"
                                data-feature="toggle_vacancy_interviews"
                                data-vacancy-id={vacancy.id}
                              >
                                {expandedVacancyIds[vacancy.id] ? '👆 Hide Interviews' : '👇 Show Interviews'}
                              </button>
                            </div>
                            
                            {/* Expanded Interview Details */}
                            {expandedVacancyIds[vacancy.id] && (
                              <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50">
                                {interviews.filter(iv => iv.VacancyId === vacancy.id).length > 0 ? (
                                  <div className="space-y-4">
                                    {interviews
                                      .filter(iv => iv.VacancyId === vacancy.id)
                                      .map((interview, idx) => (
                                        <div key={interview.id || idx} className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                                          <div className="flex justify-between items-start mb-3">
                                            <h5 className="font-medium text-gray-900">{interview.Name || 'Unnamed Candidate'}</h5>
                                            <div className="flex items-center space-x-1">
                                              {interview.Passed === 1 ? (
                                                <span className="text-xs px-2 py-1 bg-green-100 text-green-800 rounded-full">✓ Passed</span>
                                              ) : interview.Passed === 0 ? (
                                                <span className="text-xs px-2 py-1 bg-red-100 text-red-800 rounded-full">✗ Failed</span>
                                              ) : (
                                                <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">⏳ Pending</span>
                                              )}
                                            </div>
                                          </div>
                                          
                                          <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 mb-4">
                                            <div>
                                              <span className="font-medium">Date:</span> {
                                                interview.Date
                                                  ? (() => {
                                                      try {
                                                        return format(new Date(interview.Date), 'dd/MM/yyyy');
                                                      } catch {
                                                        return interview.Date;
                                                      }
                                                    })()
                                                  : 'Not set'
                                              }
                                            </div>
                                            <div>
                                              <span className="font-medium">Time:</span> {interview.Time || 'Not set'}
                                            </div>
                                            <div className="col-span-2">
                                              <span className="font-medium">Location:</span> {interview.Location || 'Not specified'}
                                            </div>
                                            {interview.TotalScore !== undefined && (
                                              <div className="col-span-2">
                                                <span className="font-medium">Score:</span> {interview.TotalScore}/100
                                              </div>
                                            )}
                                          </div>
                                          
                                          <div className="flex flex-wrap gap-2">
                                            {interview.Passed === null && (
                                              <button
                                                className="flex-1 min-w-0 inline-flex items-center justify-center px-3 py-2 rounded-lg bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-medium text-xs shadow-sm hover:shadow-lg transition-all duration-200"
                                                onClick={() => openDecisionModal(interview)}
                                                data-component="mobile-interview-card"
                                                data-feature="open_decision_modal"
                                                data-time-saved="30"
                                                data-interview-id={interview.id}
                                              >
                                                📝 Decision
                                              </button>
                                            )}
                                            {interview.Passed === 1 && interview.Accepted === null && (
                                              <>
                                                <button
                                                  className="flex-1 min-w-0 inline-flex items-center justify-center px-3 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-green-600 text-white font-medium text-xs shadow-sm hover:shadow-lg transition-all duration-200"
                                                  onClick={() => handleCandidateAccepted(interview.id)}
                                                  data-component="mobile-interview-card"
                                                  data-feature="accept_candidate_post"
                                                  data-time-saved="30"
                                                  data-interview-id={interview.id}
                                                >
                                                  ✅ Accept
                                                </button>
                                                <button
                                                  className="flex-1 min-w-0 inline-flex items-center justify-center px-3 py-2 rounded-lg bg-gradient-to-r from-gray-500 to-gray-600 text-white font-medium text-xs shadow-sm hover:shadow-lg transition-all duration-200"
                                                  onClick={() => handleCandidateRejected(interview.id)}
                                                  data-component="mobile-interview-card"
                                                  data-feature="reject_candidate_post"
                                                  data-time-saved="30"
                                                  data-interview-id={interview.id}
                                                >
                                                  ❌ Reject
                                                </button>
                                              </>
                                            )}
                                            {interview.Complete !== 1 && (
                                              <button
                                                onClick={() => handleDeleteInterview(interview.id)}
                                                data-component="mobile-interview-card"
                                                data-feature="cancel_interview_delete"
                                                data-interview-id={interview.id}
                                                className="flex-1 min-w-0 inline-flex items-center justify-center px-3 py-2 rounded-lg bg-gradient-to-r from-red-500 to-pink-600 text-white font-medium text-xs shadow-sm hover:shadow-lg transition-all duration-200"
                                              >
                                                🗑️ Cancel
                                              </button>
                                            )}
                                            {(!interview.questions || interview.questions.length === 0) ? (
                                              <button
                                                onClick={() => handleQuestionGeneration(interview.id)}
                                                disabled={generatingQuestions[interview.id]}
                                                data-component="mobile-interview-card"
                                                data-feature="generate_questions_post"
                                                data-time-saved="60"
                                                data-interview-id={interview.id}
                                                className="flex-1 min-w-0 inline-flex items-center justify-center px-3 py-2 rounded-lg bg-gradient-to-r from-emerald-500 to-green-600 text-white font-medium text-xs shadow-sm hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                              >
                                                {generatingQuestions[interview.id] ? (
                                                  <>
                                                    <svg className="animate-spin -ml-1 mr-1 h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    Generating...
                                                  </>
                                                ) : (
                                                  '🤖 Generate Questions'
                                                )}
                                              </button>
                                            ) : (
                                              <button
                                                onClick={() => handleToggleShowQuestions(interview.id)}
                                                data-component="mobile-interview-card"
                                                data-feature="toggle_question_list"
                                                data-interview-id={interview.id}
                                                className="flex-1 min-w-0 inline-flex items-center justify-center px-3 py-2 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-medium text-xs shadow-sm hover:shadow-lg transition-all duration-200"
                                              >
                                                {showQuestions[interview.id] ? '👆 Hide Questions' : '👇 Show Questions'}
                                              </button>
                                            )}
                                          </div>
                                          
                                          {/* Questions Section */}
                                          {showQuestions[interview.id] && interview.questions && interview.questions.length > 0 && (
                                            <div className="mt-4 pt-4 border-t border-gray-200">
                                              <h6 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                                                ❓ Interview Questions ({interview.questions.length})
                                              </h6>
                                              <div className="space-y-3">
                                                {interview.questions.map((question, qIdx) => (
                                                  <div key={question.id || qIdx} className="bg-gray-50 rounded-lg p-3">
                                                    <div className="flex justify-between items-start mb-2">
                                                      <span className="text-xs font-medium text-gray-500">Q{qIdx + 1}</span>
                                                      {question.score !== null && question.score !== undefined && (
                                                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                                                          question.score <= 3 ? 'bg-red-100 text-red-800' :
                                                          question.score <= 5 ? 'bg-orange-100 text-orange-800' :
                                                          question.score <= 8 ? 'bg-yellow-100 text-yellow-800' :
                                                          'bg-green-100 text-green-800'
                                                        }`}>
                                                          {question.score}/10
                                                        </span>
                                                      )}
                                                    </div>
                                                    <p className="text-sm text-gray-800 mb-2">{question.question}</p>
                                                    {question.answer && (
                                                      <p className="text-xs text-gray-600">
                                                        <span className="font-medium">Answer:</span> {question.answer}
                                                      </p>
                                                    )}
                                                    <div className="flex gap-2 mt-2">
                                                      <button
                                                        className="text-xs px-2 py-1 bg-indigo-100 text-indigo-800 rounded hover:bg-indigo-200 transition-colors"
                                                        onClick={() => handleEditQuestion(interview.id, question)}
                                                        data-component="interview-question-list"
                                                        data-feature="edit_question_toggle"
                                                        data-interview-id={interview.id}
                                                        data-question-id={question.id || qIdx}
                                                      >
                                                        ✏️ Edit
                                                      </button>
                                                      <button
                                                        className="text-xs px-2 py-1 bg-red-100 text-red-800 rounded hover:bg-red-200 transition-colors"
                                                        onClick={() => handleDeleteQuestion(interview.id, question)}
                                                        data-component="interview-question-list"
                                                        data-feature="delete_question_delete"
                                                        data-interview-id={interview.id}
                                                        data-question-id={question.id || qIdx}
                                                      >
                                                        🗑️ Delete
                                                      </button>
                                                    </div>
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                  </div>
                                ) : (
                                  <div className="text-center text-gray-500 py-8">
                                    <div className="text-4xl mb-2">📋</div>
                                    <p className="font-medium">No interviews scheduled</p>
                                    <p className="text-sm">Interviews will appear here once scheduled</p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <div className="text-6xl mb-4">🎯</div>
                      <h4 className="text-xl font-semibold text-gray-900 mb-2">No Vacancies Available</h4>
                      <p className="text-gray-600">Create some vacancies to start managing interviews</p>
                    </div>
                  )}
                </div>
                
                {/* Decision Modal */}
                {decisionModal.open && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-auto">
                      <div className="px-6 py-4 border-b border-gray-200">
                        <h3 className="text-xl font-semibold text-gray-900">Interview Decision</h3>
                        <p className="text-sm text-gray-600 mt-1">Make a decision for this interview</p>
                      </div>
                      <div className="p-6 space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-3">Decision</label>
                          <div className="flex gap-4">
                            <label className="flex items-center cursor-pointer">
                              <input
                                type="radio"
                                name="passed"
                                value="yes"
                                checked={decisionModal.passed === 'yes'}
                                onChange={handleDecisionChange}
                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                              />
                              <span className="ml-2 text-sm text-gray-900">✅ Pass</span>
                            </label>
                            <label className="flex items-center cursor-pointer">
                              <input
                                type="radio"
                                name="passed"
                                value="no"
                                checked={decisionModal.passed === 'no'}
                                onChange={handleDecisionChange}
                                className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                              />
                              <span className="ml-2 text-sm text-gray-900">❌ Fail</span>
                            </label>
                          </div>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
                          <textarea
                            name="notes"
                            rows={4}
                            className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 text-gray-900 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all duration-200"
                            value={decisionModal.notes}
                            onChange={handleDecisionChange}
                            placeholder="Add your notes about the decision..."
                          />
                        </div>
                      </div>
                      <div className="px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row justify-end gap-3">
                        <button
                          className="px-6 py-3 rounded-lg bg-gray-100 text-gray-700 font-semibold hover:bg-gray-200 transition-colors"
                          onClick={closeDecisionModal}
                          disabled={decisionModal.saving}
                          data-component="interview-decision-modal"
                          data-feature="cancel_decision_modal"
                        >
                          Cancel
                        </button>
                        <button
                          className="px-6 py-3 rounded-lg bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold hover:from-indigo-600 hover:to-purple-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed transition-all duration-200"
                          onClick={handleSaveDecision}
                          disabled={decisionModal.saving || decisionModal.passed === null}
                          data-component="interview-decision-modal"
                          data-time-saved="30"
                          data-feature="save_decision_post"
                          data-interview-id={decisionModal.interview?.id || undefined}
                        >
                          {decisionModal.saving ? '💾 Saving...' : '💾 Save Decision'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'onboarding' && (
              <div className={adminCardClassName}>
                <div className="px-6 py-4 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-600">
                  <h3 className="text-xl font-semibold text-white">✅ Candidate Onboarding</h3>
                  <p className="text-emerald-100 text-sm mt-1">Track offer acceptance, DBS checks, start date, and user creation</p>
                </div>

                <div className="p-6">
                  {loadingOnboarding ? (
                    <div className="flex justify-center py-10">
                      <LoadingComponent />
                    </div>
                  ) : onboardingCandidates.length === 0 ? (
                    <div className="text-center py-10">
                      <p className="text-lg font-semibold text-gray-800">No onboarding candidates yet</p>
                      <p className="text-sm text-gray-500 mt-2">Pass a candidate in Interviews to begin onboarding.</p>
                    </div>
                  ) : (
                    <div className="space-y-5">
                      {onboardingCandidates.map((candidate) => {
                        const acceptedYes = candidate.AcceptedPosition === 1;
                        const needsDBS = candidate.NeedDBS === 1;
                        const dbsPassed = (candidate.DBSStatus || '').toString().toLowerCase() === 'passed';
                        const userCreated = Number(candidate.NewUserCreated || 0) === 1;
                        const matchingRoles = roles.filter(
                          (role) => normalizeText(role?.Name) === normalizeText(candidate?.Role)
                        );
                        const selectedRoleId = selectedRoleByVacancy[candidate.id] || '';
                        const selectedDepartmentId = selectedDepartmentByVacancy[candidate.id] || '';
                        const canCreateUser =
                          acceptedYes &&
                          !!candidate.StartDate &&
                          !!selectedRoleId &&
                          !!selectedDepartmentId &&
                          (!needsDBS || dbsPassed) &&
                          !userCreated;

                        if (userCreated) {
                          return (
                            <div
                              key={candidate.id}
                              className="rounded-2xl border border-emerald-200 bg-gradient-to-r from-emerald-50 via-white to-teal-50 p-5 shadow-sm"
                            >
                              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-emerald-500 text-lg font-bold text-white shadow-md">
                                    ✓
                                  </div>
                                  <div>
                                    <h4 className="text-lg font-semibold text-gray-900">{candidate.CandidateName || 'Candidate'}</h4>
                                    <p className="text-sm text-emerald-700">User created</p>
                                  </div>
                                </div>
                                <span className="inline-flex items-center rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-800">
                                  Vacancy #{candidate.id}
                                </span>
                              </div>
                            </div>
                          );
                        }

                        return (
                          <div
                            key={candidate.id}
                            className="rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50/70 via-white to-cyan-50/70 p-5 shadow-sm"
                          >
                            <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                              <div>
                                <h4 className="text-lg font-semibold text-gray-900">{candidate.CandidateName || 'Candidate'}</h4>
                                <p className="text-sm text-gray-600">{candidate.Email || 'No email'} • {candidate.Role || 'Role not set'}</p>
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <span className="inline-flex items-center rounded-full bg-cyan-100 px-3 py-1 text-xs font-semibold text-cyan-800">
                                  Vacancy #{candidate.id}
                                </span>
                                <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${needsDBS ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>
                                  {needsDBS ? 'DBS Required' : 'No DBS Required'}
                                </span>
                              </div>
                            </div>

                            <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
                              <div className="rounded-xl border border-gray-200 bg-white p-4">
                                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Candidate Accepted Position</p>
                                <div className="mt-3 flex gap-3">
                                  <button
                                    type="button"
                                    onClick={() => updateOnboardingCandidate(candidate.id, { acceptedPosition: 1 })}
                                    className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${acceptedYes ? 'bg-green-600 text-white shadow' : 'bg-green-50 text-green-700 hover:bg-green-100'}`}
                                    disabled={savingOnboardingId === candidate.id}
                                  >
                                    Yes
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => updateOnboardingCandidate(candidate.id, { acceptedPosition: 0 })}
                                    className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${candidate.AcceptedPosition === 0 ? 'bg-rose-600 text-white shadow' : 'bg-rose-50 text-rose-700 hover:bg-rose-100'}`}
                                    disabled={savingOnboardingId === candidate.id}
                                  >
                                    No
                                  </button>
                                </div>
                              </div>

                              <div className="rounded-xl border border-gray-200 bg-white p-4">
                                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Candidate Start Date</p>
                                <input
                                  type="date"
                                  value={startDateDraftByVacancy[candidate.id] || ''}
                                  onChange={(e) => handleStartDateDraftChange(candidate.id, e.target.value)}
                                  onBlur={() => commitStartDateChange(candidate.id, candidate.StartDate)}
                                  disabled={!acceptedYes || savingOnboardingId === candidate.id}
                                  className="mt-3 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800 disabled:bg-gray-100 disabled:text-gray-400"
                                />
                                <p className="mt-2 text-xs text-gray-500">The date saves when you leave the field.</p>
                              </div>
                            </div>

                            <div className="mt-4 rounded-xl border border-gray-200 bg-white p-4">
                              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Confirm Role Template</p>
                              {matchingRoles.length === 0 ? (
                                <p className="mt-3 text-sm text-amber-700">No role template found for {candidate.Role || 'this role'}.</p>
                              ) : (
                                <div className="mt-3 overflow-x-auto">
                                  <table className="min-w-full text-xs sm:text-sm border-separate border-spacing-y-2">
                                    <thead>
                                      <tr>
                                        <th className="px-2 py-1 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">Name</th>
                                        <th className="px-2 py-1 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">Description</th>
                                        <th className="px-2 py-1 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">Tasks</th>
                                        <th className="px-2 py-1 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">PayRate</th>
                                        <th className="px-2 py-1 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">Department</th>
                                        <th className="px-2 py-1 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">PayType</th>
                                        <th className="px-2 py-1 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">ContractType</th>
                                        <th className="px-2 py-1 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">ContractedDays</th>
                                        <th className="px-2 py-1 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">BreakLength</th>
                                        <th className="px-2 py-1 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">ContractedHours</th>
                                        <th className="px-2 py-1 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">AgeGroup</th>
                                        <th className="px-2 py-1 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">NeedDBS</th>
                                        <th className="px-2 py-1 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">Access</th>
                                        <th className="px-2 py-1 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">ManagementLevel</th>
                                        <th className="px-2 py-1 text-right text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500">Action</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {matchingRoles.map((role) => {
                                        const isSelected = Number(selectedRoleId) === Number(role.id);
                                        return (
                                          <tr key={role.id} className={`bg-slate-50/80 ring-1 ${isSelected ? 'ring-emerald-300' : 'ring-slate-200'}`}>
                                            <td className="px-2 py-2 font-medium text-gray-900">{role.Name || '-'}</td>
                                            <td className="px-2 py-2 text-gray-700 max-w-[180px] truncate">{role.Description || '-'}</td>
                                            <td className="px-2 py-2 text-gray-700 max-w-[180px] truncate">{role.Tasks || '-'}</td>
                                            <td className="px-2 py-2 text-gray-700">{role.PayRate ?? '-'}</td>
                                            <td className="px-2 py-2 text-gray-700">{role.Department || '-'}</td>
                                            <td className="px-2 py-2 text-gray-700">{role.PayType || '-'}</td>
                                            <td className="px-2 py-2 text-gray-700">{role.ContractType || '-'}</td>
                                            <td className="px-2 py-2 text-gray-700">{role.ContractedDays || '-'}</td>
                                            <td className="px-2 py-2 text-gray-700">{role.BreakLength ?? '-'}</td>
                                            <td className="px-2 py-2 text-gray-700">{role.ContractedHours ?? '-'}</td>
                                            <td className="px-2 py-2 text-gray-700">{role.AgeGroup || '-'}</td>
                                            <td className="px-2 py-2 text-gray-700">{role.NeedDBS ?? '-'}</td>
                                            <td className="px-2 py-2 text-gray-700 capitalize">{resolveAccessFromRole(role)}</td>
                                            <td className="px-2 py-2 text-gray-700">{normalizeManagementLevel(role.ManagementLevel)}</td>
                                            <td className="px-2 py-2 text-right">
                                              <button
                                                type="button"
                                                onClick={() => setSelectedRoleByVacancy((prev) => ({ ...prev, [candidate.id]: role.id }))}
                                                className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition ${isSelected ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'}`}
                                              >
                                                {isSelected ? 'Selected' : 'Select'}
                                              </button>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>

                            <div className="mt-4 rounded-xl border border-gray-200 bg-white p-4">
                              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Select Department</p>
                              <select
                                value={selectedDepartmentId}
                                onChange={(e) => setSelectedDepartmentByVacancy((prev) => ({ ...prev, [candidate.id]: e.target.value ? Number(e.target.value) : '' }))}
                                className="mt-3 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800"
                              >
                                <option value="">Choose department</option>
                                {departments.map((department) => (
                                  <option key={department.id} value={department.id}>
                                    {department.Name}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div className="mt-4 rounded-xl border border-gray-200 bg-white p-4">
                              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Add Work Email</p>
                              <input
                                type="email"
                                value={candidateWorkEmail[candidate.id] || ''}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  setCandidateWorkEmail((prev) => ({ ...prev, [candidate.id]: value }));
                                }}
                                placeholder="name@company.com"
                                className="mt-3 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-800"
                              />
                            </div>

                            {acceptedYes && needsDBS && (
                              <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/70 p-4">
                                <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">DBS Checklist Status</p>
                                <div className="mt-3 flex flex-wrap gap-2">
                                  {['Sent', 'Complete', 'Passed', 'Failed'].map((status) => (
                                    <button
                                      key={status}
                                      type="button"
                                      onClick={() => updateOnboardingCandidate(candidate.id, { dbsStatus: status })}
                                      className={`rounded-lg px-3 py-2 text-xs font-semibold transition ${(candidate.DBSStatus || '').toString().toLowerCase() === status.toLowerCase() ? 'bg-amber-600 text-white shadow' : 'bg-white text-amber-800 border border-amber-200 hover:bg-amber-100'}`}
                                      disabled={savingOnboardingId === candidate.id}
                                    >
                                      {status}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}

                            <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                              <p className="text-xs text-gray-500">
                                {savingOnboardingId === candidate.id ? 'Saving checklist...' : 'Checklist buttons save immediately. Start date saves when you leave the field.'}
                              </p>
                              <button
                                type="button"
                                onClick={() => createOnboardingUser(candidate.id, candidate.SuccessfulCandidateId, candidate.StartDate)}
                                disabled={!canCreateUser || creatingOnboardingUserId === candidate.id}
                                className="inline-flex items-center justify-center rounded-lg bg-gradient-to-r from-indigo-600 to-cyan-600 px-5 py-2.5 text-sm font-semibold text-white shadow hover:from-indigo-700 hover:to-cyan-700 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed"
                              >
                                {creatingOnboardingUserId === candidate.id ? 'Creating User...' : 'Create User'}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 'assessments' && (
              <div className="space-y-8">
                <Assessment embedded />
              </div>
            )}
          </div>
        </div>
      </div>

      {assessmentModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-5xl bg-white rounded-3xl shadow-2xl border border-white/40 overflow-hidden">
            <div className="flex flex-col gap-2 border-b border-gray-100 bg-gradient-to-r from-indigo-600 via-purple-600 to-emerald-500 px-6 py-5 text-white">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm uppercase tracking-widest text-white/80">Assessment Results</p>
                  <h3 className="text-2xl font-bold">
                    {assessmentRecord?.candidateName || 'Candidate'}
                    {assessmentRecord?.role && (
                      <span className="ml-2 text-base font-semibold text-white/80">• {assessmentRecord.role}</span>
                    )}
                  </h3>
                  <p className="text-sm text-white/80">
                    {assessmentRecord?.assessmentId ? `Assessment ${assessmentRecord.assessmentId}` : 'No assessment ID'}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  {assessmentRecord?.status && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-white/20 text-white">
                      {assessmentRecord.status.toUpperCase()}
                    </span>
                  )}
                  <button
                    onClick={closeAssessmentModal}
                    className="inline-flex items-center justify-center rounded-full bg-white/20 p-2 text-white transition hover:bg-white/30"
                    aria-label="Close assessment details"
                  >
                    ✕
                  </button>
                </div>
              </div>
              {assessmentRecord?.difficulty && (
                <p className="text-xs font-medium text-white/80">Difficulty: {assessmentRecord.difficulty}</p>
              )}
            </div>

            <div className="p-6 space-y-6">
              {assessmentLoading && (
                <div className="py-12">
                  <LoadingComponent />
                </div>
              )}

              {!assessmentLoading && assessmentError && (
                <div className="rounded-2xl border border-rose-100 bg-rose-50 px-6 py-8 text-center">
                  <p className="text-rose-700 font-semibold">{assessmentError}</p>
                  <p className="text-sm text-rose-500 mt-2">Close this window and try again.</p>
                </div>
              )}

              {!assessmentLoading && !assessmentError && assessmentRecord && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4">
                      <p className="text-xs font-semibold text-emerald-700/70 uppercase">Total Score</p>
                      <p className="mt-2 text-3xl font-bold text-emerald-700">{assessmentRecord.totalScore ?? 0}</p>
                      <p className="text-xs text-emerald-700/70">Out of 10</p>
                    </div>
                    <div className="rounded-2xl border border-indigo-100 bg-indigo-50/80 p-4">
                      <p className="text-xs font-semibold text-indigo-700/70 uppercase">Correct Answers</p>
                      <p className="mt-2 text-3xl font-bold text-indigo-700">
                        {assessmentRecord.correctCount ?? 0}/{assessmentRecord.totalQuestions ?? 0}
                      </p>
                      <p className="text-xs text-indigo-700/70">Answered</p>
                    </div>
                    <div className="rounded-2xl border border-amber-100 bg-amber-50/80 p-4">
                      <p className="text-xs font-semibold text-amber-700/70 uppercase">Incorrect Answers</p>
                      <p className="mt-2 text-3xl font-bold text-amber-700">{assessmentRecord.incorrectCount ?? 0}</p>
                      <p className="text-xs text-amber-700/70">Need review</p>
                    </div>
                    <div className="rounded-2xl border border-gray-100 bg-gray-50/80 p-4">
                      <p className="text-xs font-semibold text-gray-600 uppercase">Completed At</p>
                      <p className="mt-2 text-sm font-semibold text-gray-800">
                        {assessmentRecord.completedAt
                          ? (() => {
                              try {
                                return format(new Date(assessmentRecord.completedAt), 'dd MMM yyyy • HH:mm');
                              } catch (err) {
                                return assessmentRecord.completedAt;
                              }
                            })()
                          : 'Not recorded'}
                      </p>
                      <p className="text-xs text-gray-500">Local timezone</p>
                    </div>
                  </div>

                  <div className="max-h-[55vh] overflow-y-auto pr-2 space-y-4">
                    {assessmentRecord.questions && assessmentRecord.questions.length > 0 ? (
                      assessmentRecord.questions.map((question, index) => {
                        const statusLabel = question.isCorrect === null
                          ? 'Unanswered'
                          : question.isCorrect
                          ? 'Correct'
                          : 'Incorrect';
                        const statusClasses =
                          question.isCorrect === null
                            ? 'bg-gray-100 text-gray-700'
                            : question.isCorrect
                            ? 'bg-emerald-100 text-emerald-800'
                            : 'bg-rose-100 text-rose-700';

                        return (
                          <div
                            key={`${question.questionNo ?? index}-${question.question?.slice(0, 20)}`}
                            className={`rounded-2xl border p-4 shadow-sm transition ${
                              question.isCorrect === null
                                ? 'border-gray-100 bg-white'
                                : question.isCorrect
                                ? 'border-emerald-100 bg-emerald-50/60'
                                : 'border-rose-100 bg-rose-50/60'
                            }`}
                          >
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div>
                                <p className="text-xs font-semibold text-gray-500 uppercase">
                                  Question {question.questionNo ?? index + 1}
                                </p>
                                <p className="mt-1 text-base font-semibold text-gray-900">{question.question || 'Question unavailable'}</p>
                              </div>
                              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${statusClasses}`}>
                                {statusLabel}
                              </span>
                            </div>

                            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                              {Object.entries(question.selections || {})
                                .filter(([, selectionText]) => Boolean(selectionText))
                                .map(([choiceKey, selectionText]) => {
                                  const isCandidate = question.candidateAnswer === choiceKey;
                                  const isCorrectChoice = question.correctAnswer === choiceKey;
                                  return (
                                    <div
                                      key={choiceKey}
                                      className={`rounded-xl border px-3 py-2 text-sm shadow-sm ${
                                        isCorrectChoice
                                          ? 'border-emerald-200 bg-white'
                                          : 'border-gray-200 bg-white'
                                      } ${isCandidate ? 'ring-2 ring-indigo-200' : ''}`}
                                    >
                                      <p className="font-semibold text-gray-900">
                                        {choiceKey}. <span className="font-normal text-gray-700">{selectionText}</span>
                                      </p>
                                      <div className="mt-1 flex flex-wrap gap-2 text-xs font-semibold">
                                        {isCandidate && (
                                          <span className="inline-flex items-center rounded-full bg-indigo-50 px-2 py-0.5 text-indigo-700">
                                            Candidate choice
                                          </span>
                                        )}
                                        {isCorrectChoice && (
                                          <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-700">
                                            Correct answer
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>

                            <div className="mt-3 text-xs text-gray-600 flex flex-wrap gap-4">
                              <span>
                                Candidate Answer: <strong>{question.candidateAnswer || '—'}</strong>
                              </span>
                              <span>
                                Correct Answer: <strong>{question.correctAnswer || '—'}</strong>
                              </span>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="rounded-2xl border border-gray-100 bg-gray-50 py-6 text-center text-gray-500">
                        No question data available for this assessment.
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      {subscriptionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl border border-white/40 overflow-hidden">
            <div className="bg-gradient-to-r from-rose-500 to-orange-500 px-6 py-5 text-white">
              <h3 className="text-xl font-bold">Subscription Limit Reached</h3>
              <p className="text-sm text-rose-100 mt-1">Your current plan does not have capacity for additional users.</p>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-700">Please upgrade your subscription to accommodate new users.</p>
              <div className="mt-5 flex gap-3">
                <button
                  onClick={() => setSubscriptionModal(false)}
                  className="flex-1 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => { setSubscriptionModal(false); navigate('/billing'); }}
                  className="flex-1 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-2.5 text-sm font-bold text-white shadow-md hover:from-indigo-700 hover:to-violet-700"
                >
                  Change &rarr;
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default PDFScanner;
